import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { alphaApi, FALLBACK_NAV, type NavItem } from './api/alpha';
import { AppShell } from './shell';
import { AlphaReadinessPage } from './pages/AlphaReadinessPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { ProfilePage } from './pages/auth/ProfilePage';
import { ActiveSessionsPage } from './pages/auth/ActiveSessionsPage';
import { ChangePasswordPage } from './pages/auth/ChangePasswordPage';
import { TrustedDevicesPage } from './pages/auth/TrustedDevicesPage';
import { SecurityDashboardPage } from './pages/auth/SecurityDashboardPage';
import { RolesPage } from './pages/authorization/RolesPage';
import { PermissionsPage } from './pages/authorization/PermissionsPage';
import { PoliciesPage } from './pages/authorization/PoliciesPage';
import { RoleAssignmentPage } from './pages/authorization/RoleAssignmentPage';
import { PermissionAuditPage } from './pages/authorization/PermissionAuditPage';
import { UserPermissionsPage } from './pages/authorization/UserPermissionsPage';
import { AuthorizationDashboardPage } from './pages/authorization/AuthorizationDashboardPage';
import { PermissionMatrixPage } from './pages/authorization/PermissionMatrixPage';
import { PolicyBuilderPage } from './pages/authorization/PolicyBuilderPage';
import { OrganizationListPage } from './pages/organizations/OrganizationListPage';
import { OrganizationCreatePage } from './pages/organizations/OrganizationCreatePage';
import { OrganizationDetailPage } from './pages/organizations/OrganizationDetailPage';
import { OrganizationDashboardPage } from './pages/organizations/OrganizationDashboardPage';
import { OrganizationSearchPage } from './pages/organizations/OrganizationSearchPage';
import { TenantListPage } from './pages/tenants/TenantListPage';
import { TenantCreatePage } from './pages/tenants/TenantCreatePage';
import { TenantDetailPage } from './pages/tenants/TenantDetailPage';
import { TenantDashboardPage } from './pages/tenants/TenantDashboardPage';
import { TenantSearchPage } from './pages/tenants/TenantSearchPage';
import { WorkspaceListPage } from './pages/workspaces/WorkspaceListPage';
import { WorkspaceCreatePage } from './pages/workspaces/WorkspaceCreatePage';
import { WorkspaceDetailPage } from './pages/workspaces/WorkspaceDetailPage';
import { WorkspaceDashboardPage } from './pages/workspaces/WorkspaceDashboardPage';
import { WorkspaceAnalyticsPage } from './pages/workspaces/WorkspaceAnalyticsPage';
import { WorkspaceSearchPage } from './pages/workspaces/WorkspaceSearchPage';
import { UserDirectoryPage } from './pages/users/UserDirectoryPage';
import { UserCreatePage } from './pages/users/UserCreatePage';
import { UserDetailPage } from './pages/users/UserDetailPage';
import { InvitationsPage } from './pages/users/InvitationsPage';
import { IdentityDirectoryPage } from './pages/identity/IdentityDirectoryPage';
import { IdentityDetailsPage } from './pages/identity/IdentityDetailsPage';
import { IdentityActivityPage } from './pages/identity/IdentityActivityPage';
import { IdentityAuditPage } from './pages/identity/IdentityAuditPage';
import { IdentityProfilePage } from './pages/identity/IdentityProfilePage';
import { IdentityPreferencesPage } from './pages/identity/IdentityPreferencesPage';
import { IdentitySearchPage } from './pages/identity/IdentitySearchPage';
import { TeamsPage } from './pages/users/TeamsPage';
import { MemberDirectoryPage } from './pages/collaboration/MemberDirectoryPage';
import { MemberSearchPage } from './pages/collaboration/MemberSearchPage';
import { MembershipManagementPage } from './pages/collaboration/MembershipManagementPage';
import { MemberDetailPage } from './pages/collaboration/MemberDetailPage';
import { TeamDetailPage } from './pages/collaboration/TeamDetailPage';
import { TeamDashboardPage } from './pages/collaboration/TeamDashboardPage';
import { BulkInvitationPage } from './pages/collaboration/BulkInvitationPage';
import { ProductListPage } from './pages/products/ProductListPage';
import { ProductDetailPage } from './pages/products/ProductDetailPage';
import { ProductDashboardPage } from './pages/products/ProductDashboardPage';
import { ProductSearchPage } from './pages/products/ProductSearchPage';
import { ProductEditorPage } from './pages/products/ProductEditorPage';
import { ProductApprovalPage } from './pages/products/ProductApprovalPage';
import { ProductHealthPage } from './pages/products/ProductHealthPage';
import { ProductRelationshipsPage } from './pages/products/ProductRelationshipsPage';
import { ProductWorkspacePage } from './pages/products/ProductWorkspacePage';
import { MarketplaceDNAEnginePage } from './pages/products/MarketplaceDNAEnginePage';
import { ListingGrowthIntelligencePage } from './pages/products/ListingGrowthIntelligencePage';
import { AdvertisingGrowthIntelligencePage } from './pages/advertising/AdvertisingGrowthIntelligencePage';
import { FBAGrowthIntelligencePage } from './pages/fba/FBAGrowthIntelligencePage';
import { ProfitIntelligencePage } from './pages/profit/ProfitIntelligencePage';
import { MarketplaceOperatingBrainPage } from './pages/mob/MarketplaceOperatingBrainPage';
import { ProductionReadinessPage } from './pages/prt/ProductionReadinessPage';
import { RecommendationValidationPage } from './pages/val/RecommendationValidationPage';
import { IntelligenceQAPage } from './pages/iqa/IntelligenceQAPage';
import { AutonomousOperationsPage } from './pages/aow/AutonomousOperationsPage';
import { AIGovernancePage } from './pages/gov/AIGovernancePage';
import { EngineeringExcellencePage } from './pages/exl/EngineeringExcellencePage';
import { AssetLibraryPage } from './pages/media/AssetLibraryPage';
import { AssetDetailPage } from './pages/media/AssetDetailPage';
import { AssetSearchPage } from './pages/media/AssetSearchPage';
import { BulkUploadPage } from './pages/media/BulkUploadPage';
import { ApprovalQueuePage } from './pages/media/ApprovalQueuePage';
import { DuplicateManagerPage } from './pages/media/DuplicateManagerPage';
import { QualityDashboardPage } from './pages/media/QualityDashboardPage';
import { PromptLibraryPage } from './pages/media/PromptLibraryPage';
import { PromptEditorPage } from './pages/media/PromptEditorPage';
import { GenerationHistoryPage } from './pages/media/GenerationHistoryPage';
import { CatalogListPage } from './pages/catalog/CatalogListPage';
import { CatalogDashboardPage as EnterpriseCatalogDashboardPage } from './pages/catalog/CatalogDashboardPage';
import { CategoryTreePage } from './pages/catalog/CategoryTreePage';
import { CategoryEditorPage } from './pages/catalog/CategoryEditorPage';
import { MarketplaceMappingPage } from './pages/catalog/MarketplaceMappingPage';
import { CollectionBuilderPage } from './pages/catalog/CollectionBuilderPage';
import { SmartCollectionBuilderPage } from './pages/catalog/SmartCollectionBuilderPage';
import { ClassificationDashboardPage } from './pages/catalog/ClassificationDashboardPage';
import { AIClassificationReviewPage } from './pages/catalog/AIClassificationReviewPage';
import { SEOCategoriesPage } from './pages/catalog/SEOCategoriesPage';
import { TaxonomyExplorerPage } from './pages/catalog/TaxonomyExplorerPage';
import { HSNMappingPage } from './pages/catalog/HSNMappingPage';
import { GSTClassificationPage } from './pages/catalog/GSTClassificationPage';
import { ProductStructureHubPage } from './pages/productstructure/ProductStructureHubPage';
import { VariantManagerPage, VariantMatrixPage } from './pages/productstructure/VariantPages';
import { BundleBuilderPage, KitBuilderPage } from './pages/productstructure/BundlePages';
import { RelationshipManagerPage, ProductFamilyPage, MarketplaceStructurePage, DependencyGraphPage } from './pages/productstructure/RelationshipPages';
import { ProductAttributesHubPage, AttributeLibraryPage, AttributeBuilderPage } from './pages/productattributes/AttributePages';
import { AttributeSetsPage, TemplateManagerPage, SpecificationBuilderPage } from './pages/productattributes/SetAndSpecPages';
import { MarketplaceAttributeMappingPage, ValidationDashboardPage, AIRecommendationsPage, LocalizationPage, UnitsOfMeasurePage } from './pages/productattributes/IntelligencePages';
import { PricingHubPage } from './pages/pricing/PricingHubPage';
import { PriceBookManagerPage, BulkPriceUpdatePage, MarketplacePricingPage } from './pages/pricing/PriceBookPages';
import { PromotionManagerPage, DiscountManagerPage, CouponManagerPage } from './pages/pricing/PromotionPages';
import { PricingAnalyticsPage, MarginDashboardPage, AIPricingRecommendationsPage, CompetitorPricingPage } from './pages/pricing/AnalyticsPages';
import { InventoryHubPage } from './pages/inventory/InventoryHubPage';
import { WarehouseDashboardPage as IMSWarehouseDashboardPage, WarehouseManagerPage } from './pages/inventory/WarehousePages';
import { InventoryDashboardPage as IMSInventoryDashboardPage, InventoryLedgerPage, MarketplaceInventoryPage as IMSMarketplaceInventoryPage } from './pages/inventory/InventoryPages';
import {
  StockTransferPage,
  ReservationManagerPage,
  AllocationManagerPage,
  CycleCountPage,
  PhysicalVerificationPage,
  InventoryForecastPage,
  InventoryHealthPage,
} from './pages/inventory/OperationsPages';
import { ListingsHubPage } from './pages/listings/ListingsHubPage';
import { ListingManagerPage, ListingEditorPage, MarketplacePreviewPage } from './pages/listings/ListingPages';
import {
  ListingHealthDashboardPage,
  SEODashboardPage,
  ComplianceDashboardPage,
  BulkListingGeneratorPage,
  VersionComparisonPage,
  MarketplaceTemplatesPage,
  AIRecommendationsPanelPage,
} from './pages/listings/IntelligencePages';
import { MarketplaceSyncHubPage } from './pages/marketplacesync/MarketplaceSyncHubPage';
import { MarketplaceAccountHubPage } from './pages/marketplaceaccount/MarketplaceAccountHubPage';
import { ConnectionWizardPage } from './pages/marketplaceaccount/ConnectionWizardPage';
import { ConnectionsPage } from './pages/marketplaceaccount/ConnectionsPage';
import { CredentialManagerPage } from './pages/marketplaceaccount/CredentialManagerPage';
import { HealthDashboardPage } from './pages/marketplaceaccount/HealthDashboardPage';
import { ChannelSettingsPage } from './pages/marketplaceaccount/ChannelSettingsPage';
import { MappingsPage } from './pages/marketplaceaccount/MappingsPage';
import { OrderHubPage } from './pages/orders/OrderHubPage';
import { OrderListPage } from './pages/orders/OrderListPage';
import { OrderDetailPage } from './pages/orders/OrderDetailPage';
import { OrderImportPage } from './pages/orders/OrderImportPage';
import { AllocationDashboardPage } from './pages/orders/AllocationDashboardPage';
import { OrderAnalyticsPage } from './pages/orders/OrderAnalyticsPage';
import { SLADashboardPage } from './pages/orders/SLADashboardPage';
import { WarehouseHubPage } from './pages/warehouse/WarehouseHubPage';
import { PickingDashboardPage } from './pages/warehouse/PickingDashboardPage';
import { PackingDashboardPage } from './pages/warehouse/PackingDashboardPage';
import { DispatchDashboardPage } from './pages/warehouse/DispatchDashboardPage';
import { DockDashboardPage } from './pages/warehouse/DockDashboardPage';
import { WorkerDashboardPage } from './pages/warehouse/WorkerDashboardPage';
import { WavePlannerPage } from './pages/warehouse/WavePlannerPage';
import { WarehouseKPIPage } from './pages/warehouse/WarehouseKPIPage';
import { MobileWarehousePage } from './pages/warehouse/MobileWarehousePage';
import { LogisticsHubPage } from './pages/logistics/LogisticsHubPage';
import { ShipmentsPage } from './pages/logistics/ShipmentsPage';
import { CarriersPage } from './pages/logistics/CarriersPage';
import { TrackingPage } from './pages/logistics/TrackingPage';
import { RateComparisonPage } from './pages/logistics/RateComparisonPage';
import { ServiceabilityPage } from './pages/logistics/ServiceabilityPage';
import { LogisticsAnalyticsPage } from './pages/logistics/LogisticsAnalyticsPage';
import { ReturnsHubPage } from './pages/returns/ReturnsHubPage';
import { ReturnsListPage } from './pages/returns/ReturnsListPage';
import { RMADashboardPage } from './pages/returns/RMADashboardPage';
import { RefundDashboardPage } from './pages/returns/RefundDashboardPage';
import { ReverseLogisticsPage } from './pages/returns/ReverseLogisticsPage';
import { ClaimsDashboardPage } from './pages/returns/ClaimsDashboardPage';
import { RTODashboardPage } from './pages/returns/RTODashboardPage';
import { ReturnsAnalyticsPage } from './pages/returns/ReturnsAnalyticsPage';
import { FinanceHubPage } from './pages/finance/FinanceHubPage';
import { SettlementDashboardPage } from './pages/finance/SettlementDashboardPage';
import { ReconciliationDashboardPage } from './pages/finance/ReconciliationDashboardPage';
import { AccountingDashboardPage } from './pages/finance/AccountingDashboardPage';
import { GSTDashboardPage } from './pages/finance/GSTDashboardPage';
import { TallySyncDashboardPage } from './pages/finance/TallySyncDashboardPage';
import { FinanceAnalyticsPage } from './pages/finance/FinanceAnalyticsPage';
import { ProcurementHubPage } from './pages/procurement/ProcurementHubPage';
import { MRPPlanningPage } from './pages/procurement/MRPPlanningPage';
import { RequisitionPage } from './pages/procurement/RequisitionPage';
import { RFQManagerPage } from './pages/procurement/RFQManagerPage';
import { PurchaseOrderPage } from './pages/procurement/PurchaseOrderPage';
import { GoodsReceiptPage } from './pages/procurement/GoodsReceiptPage';
import { ProcurementAnalyticsPage } from './pages/procurement/ProcurementAnalyticsPage';
import { SupplierHubPage } from './pages/suppliers/SupplierHubPage';
import { SupplierDirectoryPage } from './pages/suppliers/SupplierDirectoryPage';
import { SupplierOnboardingPage } from './pages/suppliers/SupplierOnboardingPage';
import { ContractManagerPage } from './pages/suppliers/ContractManagerPage';
import { ComplianceDashboardPage as SupplierComplianceDashboardPage } from './pages/suppliers/ComplianceDashboardPage';
import { PerformanceDashboardPage } from './pages/suppliers/PerformanceDashboardPage';
import { RiskDashboardPage } from './pages/suppliers/RiskDashboardPage';
import { SupplierAnalyticsPage } from './pages/suppliers/SupplierAnalyticsPage';
import { CustomerHubPage } from './pages/customers/CustomerHubPage';
import { CustomerDirectoryPage } from './pages/customers/CustomerDirectoryPage';
import { Customer360Page } from './pages/customers/Customer360Page';
import { SegmentationDashboardPage } from './pages/customers/SegmentationDashboardPage';
import { LoyaltyDashboardPage } from './pages/customers/LoyaltyDashboardPage';
import { WalletDashboardPage } from './pages/customers/WalletDashboardPage';
import { SupportCenterPage } from './pages/customers/SupportCenterPage';
import { CustomerAnalyticsPage } from './pages/customers/CustomerAnalyticsPage';
import { RuntimeHubPage } from './pages/runtime/RuntimeHubPage';
import { ModuleManagerPage } from './pages/runtime/ModuleManagerPage';
import { InstalledModulesPage } from './pages/runtime/InstalledModulesPage';
import { DependencyGraphPage as RuntimeDependencyGraphPage } from './pages/runtime/DependencyGraphPage';
import { RuntimeHealthPage } from './pages/runtime/RuntimeHealthPage';
import { RuntimeActivityPage } from './pages/runtime/RuntimeActivityPage';
import { UpgradeManagerPage } from './pages/runtime/UpgradeManagerPage';
import {
  PublicationQueuePage,
  FeedMonitorPage,
  SyncDashboardPage,
  RetryQueuePage,
  DeadLetterQueuePage,
  ConnectorManagerPage,
  WebhookMonitorPage,
} from './pages/marketplacesync/SyncPages';
import { GuestRoute, ProtectedLayout, ProtectedRoute } from './components/ProtectedRoute';
import { OnboardingWizardPage } from './pages/onboarding/OnboardingWizardPage';
import { DashboardPage } from './pages/DashboardPage';
import { ModulePlaceholderPage } from './pages/ModulePlaceholderPage';
import { CreativeStudioDashboardPage } from './pages/ai/CreativeStudioDashboardPage';
import { ListingOptimizerDashboardPage } from './pages/ai/ListingOptimizerDashboardPage';
import { DecisionIntelligenceDashboardPage } from './pages/analytics/DecisionIntelligenceDashboardPage';
import { BIHubPage } from './pages/analytics/BIHubPage';
import { ExecutiveDashboardPage } from './pages/analytics/ExecutiveDashboardPage';
import { KPIManagerPage } from './pages/analytics/KPIManagerPage';
import { ForecastCenterPage } from './pages/analytics/ForecastCenterPage';
import { DecisionCenterPage } from './pages/analytics/DecisionCenterPage';
import { InsightsPage } from './pages/analytics/InsightsPage';
import { SemanticSearchPage } from './pages/analytics/SemanticSearchPage';
import { BillingDashboardPage } from './pages/billing/BillingDashboardPage';
import { CommercialHubPage } from './pages/commercial/CommercialHubPage';
import { LicenseManagerPage } from './pages/commercial/LicenseManagerPage';
import { BundleManagerPage } from './pages/commercial/BundleManagerPage';
import { UsageDashboardPage, CreditWalletPage } from './pages/commercial/UsageAndCreditsPage';
import { ConfigurationHubPage } from './pages/configuration/ConfigurationHubPage';
import { SettingsManagerPage } from './pages/configuration/SettingsManagerPage';
import { FeatureFlagManagerPage } from './pages/configuration/FeatureFlagManagerPage';
import { SecretVaultPage } from './pages/configuration/SecretVaultPage';
import { LocalizationManagerPage } from './pages/configuration/LocalizationManagerPage';
import { PreferencesCenterPage } from './pages/configuration/PreferencesCenterPage';
import { ConfigurationHistoryPage } from './pages/configuration/ConfigurationHistoryPage';
import { ImportExportWizardPage } from './pages/configuration/ImportExportWizardPage';
import { EventHubPage } from './pages/events/EventHubPage';
import { LiveEventStreamPage } from './pages/events/LiveEventStreamPage';
import { EventExplorerPage } from './pages/events/EventExplorerPage';
import { SchemaRegistryPage } from './pages/events/SchemaRegistryPage';
import { QueueMonitorPage } from './pages/events/QueueMonitorPage';
import { DeadLetterViewerPage } from './pages/events/DeadLetterViewerPage';
import { ReplayManagerPage } from './pages/events/ReplayManagerPage';
import { CommerceEventCatalogPage } from './pages/events/CommerceEventCatalogPage';
import { MessagingReliabilityPage } from './pages/events/MessagingReliabilityPage';
import { EventMeshGovernancePage } from './pages/events/EventMeshGovernancePage';
import { EnterpriseModuleDashboardPage } from './pages/modules/EnterpriseModuleDashboardPage';
import { WorkflowCenterPage } from './pages/workflows/WorkflowCenterPage';
import { SagaDashboardPage } from './pages/workflows/SagaDashboardPage';
import { ArchitectureConsolidationPage } from './pages/architecture/ArchitectureConsolidationPage';
import { CanonicalDomainRegistryPage } from './pages/architecture/CanonicalDomainRegistryPage';
import { EnterpriseComplianceCenterPage } from './pages/architecture/EnterpriseComplianceCenterPage';
import { PluginHubPage } from './pages/plugins/PluginHubPage';
import { PluginManagerPage } from './pages/plugins/PluginManagerPage';
import { PluginMarketplacePage } from './pages/plugins/PluginMarketplacePage';
import { PublisherDashboardPage } from './pages/plugins/PublisherDashboardPage';
import { PluginHealthPage } from './pages/plugins/PluginHealthPage';
import { AutomationHubPage } from './pages/automation/AutomationHubPage';
import { WorkflowDesignerPage } from './pages/automation/WorkflowDesignerPage';
import { RuleBuilderPage } from './pages/automation/RuleBuilderPage';
import { ExecutionMonitorPage } from './pages/automation/ExecutionMonitorPage';
import { ApprovalCenterPage } from './pages/automation/ApprovalCenterPage';
import { AgentConsolePage } from './pages/automation/AgentConsolePage';
import { TemplateLibraryPage } from './pages/automation/TemplateLibraryPage';
import { CommunicationHubPage } from './pages/communication/CommunicationHubPage';
import { CampaignManagerPage } from './pages/communication/CampaignManagerPage';
import { TemplateBuilderPage } from './pages/communication/TemplateBuilderPage';
import { NotificationCenterPage } from './pages/communication/NotificationCenterPage';
import { ProviderManagerPage } from './pages/communication/ProviderManagerPage';
import { DeliveryMonitorPage } from './pages/communication/DeliveryMonitorPage';
import { CommunicationAnalyticsPage } from './pages/communication/CommunicationAnalyticsPage';
import { DocumentCenterPage } from './pages/documents/DocumentCenterPage';
import { RepositoryBrowserPage } from './pages/documents/RepositoryBrowserPage';
import { FolderManagerPage } from './pages/documents/FolderManagerPage';
import { OCRReviewPage } from './pages/documents/OCRReviewPage';
import { AIExtractionViewerPage } from './pages/documents/AIExtractionViewerPage';
import { SignatureCenterPage } from './pages/documents/SignatureCenterPage';
import { SearchConsolePage } from './pages/documents/SearchConsolePage';
import { ComplianceDashboardPage as DocumentComplianceDashboardPage } from './pages/documents/ComplianceDashboardPage';
import { DeveloperPortalPage } from './pages/developer/DeveloperPortalPage';
import { APIGatewayGovernancePage } from './pages/developer/APIGatewayGovernancePage';
import { PersistenceGovernancePage } from './pages/persistence/PersistenceGovernancePage';
import { SemanticGovernancePage } from './pages/semantic/SemanticGovernancePage';
import { AIPlatformOperationsPage } from './pages/aiplatform/AIPlatformOperationsPage';
import { CommercialGovernancePage } from './pages/commercial/CommercialGovernancePage';
import { ObservabilityGovernancePage } from './pages/observability/ObservabilityGovernancePage';
import { FoundationCertificationPage } from './pages/foundation/FoundationCertificationPage';
import { ProductFamilyConstitutionPage } from './pages/governance/ProductFamilyConstitutionPage';
import { EngineeringExecutionBiblePage } from './pages/governance/EngineeringExecutionBiblePage';
import { EngineeringBacklogSprintPlanningPage } from './pages/governance/EngineeringBacklogSprintPlanningPage';
import { ArchitectureDecisionRecordsPage } from './pages/governance/ArchitectureDecisionRecordsPage';
import { RepositoryArchitectureBiblePage } from './pages/governance/RepositoryArchitectureBiblePage';
import { RepositoryBootstrapProductionFoundationPage } from './pages/governance/RepositoryBootstrapProductionFoundationPage';
import { EnterpriseIdentityPlatformPage } from './pages/governance/EnterpriseIdentityPlatformPage';
import { MarketplaceIntegrationPlatformPage } from './pages/governance/MarketplaceIntegrationPlatformPage';
import { DailyGrowthCommandCenterDev103Page } from './pages/governance/DailyGrowthCommandCenterDev103Page';
import { ProductWorkspaceDev104Page } from './pages/governance/ProductWorkspaceDev104Page';
import { MarketplaceDNAEngineDev105Page } from './pages/governance/MarketplaceDNAEngineDev105Page';
import { ListingGrowthIntelligenceDev106Page } from './pages/governance/ListingGrowthIntelligenceDev106Page';
import { AdvertisingGrowthIntelligenceDev107Page } from './pages/governance/AdvertisingGrowthIntelligenceDev107Page';
import { FBAGrowthIntelligenceDev108Page } from './pages/governance/FBAGrowthIntelligenceDev108Page';
import { ProfitIntelligenceDev109Page } from './pages/governance/ProfitIntelligenceDev109Page';
import { MarketplaceOperatingBrainDev110Page } from './pages/governance/MarketplaceOperatingBrainDev110Page';
import { ProductionReadinessPRT001Page } from './pages/governance/ProductionReadinessPRT001Page';
import { RecommendationValidationVal001Page } from './pages/governance/RecommendationValidationVal001Page';
import { IntelligenceQAIqa001Page } from './pages/governance/IntelligenceQAIqa001Page';
import { AutonomousOperationsAow001Page } from './pages/governance/AutonomousOperationsAow001Page';
import { AIGovernanceGov001Page } from './pages/governance/AIGovernanceGov001Page';
import { EngineeringExcellenceExl001Page } from './pages/governance/EngineeringExcellenceExl001Page';
import { CustomerValuePage } from './pages/cvd/CustomerValuePage';
import { CustomerValueCvd001Page } from './pages/governance/CustomerValueCvd001Page';
import { ExperienceDesignPage } from './pages/xdb/ExperienceDesignPage';
import { ExperienceDesignXdb001Page } from './pages/governance/ExperienceDesignXdb001Page';
import { ScreenReviewPage } from './pages/xdb/ScreenReviewPage';
import { ScreenReviewXdb002Page } from './pages/governance/ScreenReviewXdb002Page';
import { InternalAlphaPage } from './pages/alpha/InternalAlphaPage';
import { InternalAlphaAlpha001Page } from './pages/governance/InternalAlphaAlpha001Page';
import { EnterpriseDesignSystemPage } from './pages/governance/EnterpriseDesignSystemPage';
import { MarketplaceIntelligenceBiblePage } from './pages/marketplace/MarketplaceIntelligenceBiblePage';
import { MarketplaceKPIBiblePage } from './pages/marketplace/MarketplaceKPIBiblePage';
import { MarketplaceDNABiblePage } from './pages/marketplace/MarketplaceDNABiblePage';
import { MarketplaceGrowthExperimentBiblePage } from './pages/marketplace/MarketplaceGrowthExperimentBiblePage';
import { MarketplaceMOSBiblePage } from './pages/marketplace/MarketplaceMOSBiblePage';
import { MarketplaceMSIBiblePage } from './pages/marketplace/MarketplaceMSIBiblePage';
import { MarketplaceMVPBiblePage } from './pages/marketplace/MarketplaceMVPBiblePage';
import { MarketplaceScreenBiblePage } from './pages/marketplace/MarketplaceScreenBiblePage';
import { MarketplaceCapabilityArchitecturePage } from './pages/marketplace/MarketplaceCapabilityArchitecturePage';
import { MarketplaceIntelligenceV1PRDPage } from './pages/marketplace/MarketplaceIntelligenceV1PRDPage';
import { DailyGrowthCommandCenterPRDPage } from './pages/marketplace/DailyGrowthCommandCenterPRDPage';
import { ProductWorkspaceASIN360PRDPage } from './pages/marketplace/ProductWorkspaceASIN360PRDPage';
import { ListingGrowthEnginePRDPage } from './pages/marketplace/ListingGrowthEnginePRDPage';
import { AdvertisingGrowthEnginePRDPage } from './pages/marketplace/AdvertisingGrowthEnginePRDPage';
import { FBAGrowthEnginePRDPage } from './pages/marketplace/FBAGrowthEnginePRDPage';
import { ProfitIntelligenceLeakageEnginePRDPage } from './pages/marketplace/ProfitIntelligenceLeakageEnginePRDPage';
import { GrowthExperimentEnginePRDPage } from './pages/marketplace/GrowthExperimentEnginePRDPage';
import { MarketplaceOperatingBrainPRDPage } from './pages/marketplace/MarketplaceOperatingBrainPRDPage';
import { CommandCenterPage } from './pages/command/CommandCenterPage';
import { GovernancePlatformPage } from './pages/governance/GovernancePlatformPage';
import { SREPlatformPage } from './pages/sre/SREPlatformPage';
import { IntegrationHubDashboardPage } from './pages/integrations/IntegrationHubDashboardPage';
import { IntegrationGovernancePage } from './pages/integrations/IntegrationGovernancePage';
import { APIGatewayPage } from './pages/integrations/APIGatewayPage';
import { DeveloperPortalPage as IntegrationsDeveloperPortalPage } from './pages/integrations/DeveloperPortalPage';
import { WebhookManagerPage } from './pages/integrations/WebhookManagerPage';
import { ConnectorMarketplacePage } from './pages/integrations/ConnectorMarketplacePage';
import { ConnectorHealthPage } from './pages/integrations/ConnectorHealthPage';
import { ConnectorLogsPage } from './pages/integrations/ConnectorLogsPage';
import { APIAnalyticsPage } from './pages/integrations/APIAnalyticsPage';
import { TransformationDesignerPage } from './pages/integrations/TransformationDesignerPage';
import { KnowledgePlatformPage } from './pages/knowledge/KnowledgePlatformPage';
import { KnowledgeHubPage } from './pages/knowledge/KnowledgeHubPage';
import { KnowledgeEditorPage } from './pages/knowledge/KnowledgeEditorPage';
import { KnowledgeGraphViewerPage } from './pages/knowledge/KnowledgeGraphViewerPage';
import { SemanticSearchPage as KnowledgeSemanticSearchPage } from './pages/knowledge/SemanticSearchPage';
import { MemoryViewerPage } from './pages/knowledge/MemoryViewerPage';
import { CitationExplorerPage } from './pages/knowledge/CitationExplorerPage';
import { KnowledgeAnalyticsPage } from './pages/knowledge/KnowledgeAnalyticsPage';
import { OperationsCenterPage } from './pages/observability/OperationsCenterPage';
import { HealthDashboardPage as ObservabilityHealthDashboardPage } from './pages/observability/HealthDashboardPage';
import { MetricsExplorerPage } from './pages/observability/MetricsExplorerPage';
import { LogExplorerPage } from './pages/observability/LogExplorerPage';
import { TraceViewerPage } from './pages/observability/TraceViewerPage';
import { IncidentCenterPage } from './pages/observability/IncidentCenterPage';
import { AlertManagerPage } from './pages/observability/AlertManagerPage';
import { RunbookCenterPage } from './pages/observability/RunbookCenterPage';
import { CapacityDashboardPage } from './pages/observability/CapacityDashboardPage';
import { PerformanceDashboardPage as ObservabilityPerformanceDashboardPage } from './pages/observability/PerformanceDashboardPage';
import { SaaSHubPage } from './pages/saas/SaaSHubPage';
import { TenantManagerPage } from './pages/saas/TenantManagerPage';
import { OrganizationManagerPage } from './pages/saas/OrganizationManagerPage';
import { BrandingCenterPage } from './pages/saas/BrandingCenterPage';
import { ProvisioningCenterPage } from './pages/saas/ProvisioningCenterPage';
import { SupportConsolePage } from './pages/saas/SupportConsolePage';
import { TenantHealthPage } from './pages/saas/TenantHealthPage';
import { TenantAnalyticsPage } from './pages/saas/TenantAnalyticsPage';
import { WhiteLabelManagerPage } from './pages/saas/WhiteLabelManagerPage';
import { MarketplaceManagerPage } from './pages/saas/MarketplaceManagerPage';
import { DecisionHubPage } from './pages/decision/DecisionHubPage';
import { RecommendationCenterPage } from './pages/decision/RecommendationCenterPage';
import { SimulationStudioPage } from './pages/decision/SimulationStudioPage';
import { OptimizationCenterPage } from './pages/decision/OptimizationCenterPage';
import { AIAgentConsolePage } from './pages/decision/AIAgentConsolePage';
import { DecisionTimelinePage } from './pages/decision/DecisionTimelinePage';
import { RiskDashboardPage as DecisionRiskDashboardPage } from './pages/decision/RiskDashboardPage';
import { ApprovalDashboardPage } from './pages/decision/ApprovalDashboardPage';
import { ExecutiveCockpitPage } from './pages/decision/ExecutiveCockpitPage';
import { ResilienceHubPage } from './pages/resilience/ResilienceHubPage';
import { BackupCenterPage } from './pages/resilience/BackupCenterPage';
import { RecoveryCenterPage } from './pages/resilience/RecoveryCenterPage';
import { ReplicationMonitorPage } from './pages/resilience/ReplicationMonitorPage';
import { DisasterRecoveryConsolePage } from './pages/resilience/DisasterRecoveryConsolePage';
import { RecoveryRunbooksPage } from './pages/resilience/RecoveryRunbooksPage';
import { IntegrityDashboardPage } from './pages/resilience/IntegrityDashboardPage';
import { BusinessContinuityDashboardPage } from './pages/resilience/BusinessContinuityDashboardPage';
import { RestoreWizardPage } from './pages/resilience/RestoreWizardPage';
import { SecurityHubPage } from './pages/security/SecurityHubPage';
import { ThreatCenterPage } from './pages/security/ThreatCenterPage';
import { IncidentCenterPage as SecurityIncidentCenterPage } from './pages/security/IncidentCenterPage';
import { IdentityGovernancePage } from './pages/security/IdentityGovernancePage';
import { RiskCenterPage } from './pages/security/RiskCenterPage';
import { ComplianceCenterPage } from './pages/security/ComplianceCenterPage';
import { AuditExplorerPage } from './pages/security/AuditExplorerPage';
import { SecretsVaultPage } from './pages/security/SecretsVaultPage';
import { PolicyManagerPage } from './pages/security/PolicyManagerPage';
import { InvestigationConsolePage } from './pages/security/InvestigationConsolePage';
import { OptimizationHubPage } from './pages/optimization/OptimizationHubPage';
import { RecommendationCenterPage as OptimizationRecommendationCenterPage } from './pages/optimization/RecommendationCenterPage';
import { CostIntelligencePage } from './pages/optimization/CostIntelligencePage';
import { CapacityPlanningPage } from './pages/optimization/CapacityPlanningPage';
import { PerformanceEngineeringPage } from './pages/optimization/PerformanceEngineeringPage';
import { SelfHealingCenterPage } from './pages/optimization/SelfHealingCenterPage';
import { BenchmarkDashboardPage } from './pages/optimization/BenchmarkDashboardPage';
import { PlatformHealthOverviewPage } from './pages/optimization/PlatformHealthOverviewPage';
import { ExecutionTimelinePage } from './pages/optimization/ExecutionTimelinePage';
import { OrchestrationDashboardPage } from './pages/workflows/OrchestrationDashboardPage';
import { PublishingDashboardPage } from './pages/publishing/PublishingDashboardPage';
import { AmazonAdvertisingIntelPage } from './pages/amazon/AmazonAdvertisingIntelPage';
import { AmazonGrowthPage } from './pages/amazon/AmazonGrowthPage';
import { InventoryIntelPage } from './pages/amazon/InventoryIntelPage';
import { MarketIntelPage } from './pages/amazon/MarketIntelPage';
import { VoCIntelPage } from './pages/amazon/VoCIntelPage';
import { ContentIntelPage } from './pages/amazon/ContentIntelPage';
import { GrowthPlannerPage } from './pages/strategy/GrowthPlannerPage';
import { PlanningPlatformPage } from './pages/planning/PlanningPlatformPage';
import { DemandPlanningPage } from './pages/planning/DemandPlanningPage';
import { ForecastEnginePage } from './pages/planning/ForecastEnginePage';
import { AIForecastPage } from './pages/planning/AIForecastPage';
import { InventoryPlanningPage } from './pages/planning/InventoryPlanningPage';
import { ProcurementPlanningPage } from './pages/planning/ProcurementPlanningPage';
import { SupplyPlanningPage } from './pages/planning/SupplyPlanningPage';
import { WarehouseCapacityPlanningPage } from './pages/planning/WarehouseCapacityPlanningPage';
import { ScenarioPlanningPage } from './pages/planning/ScenarioPlanningPage';
import { FinancialPlanningPage } from './pages/planning/FinancialPlanningPage';
import { SOPPlanningPage } from './pages/planning/SOPPlanningPage';
import { PlanningAnalyticsPage } from './pages/planning/PlanningAnalyticsPage';
import { ExecutiveCockpitPage as PlanningExecutiveCockpitPage } from './pages/planning/ExecutiveCockpitPage';
import { PlanningDecisionIntelPage } from './pages/planning/PlanningDecisionIntelPage';
import { AutonomousPlanningPage } from './pages/planning/AutonomousPlanningPage';
import { BrandPortfolioPage } from './pages/strategy/BrandPortfolioPage';
import { SearchIntelPage } from './pages/amazon/SearchIntelPage';
import { ExperimentIntelPage } from './pages/amazon/ExperimentIntelPage';
import { DailyOpsPage } from './pages/executive/DailyOpsPage';
import { OpsHubPage } from './pages/operations/OpsHubPage';
import { FinIntelPage } from './pages/finance/FinIntelPage';
import { CatalogOpsPage } from './pages/amazon/CatalogOpsPage';
import { OrderOpsPage } from './pages/amazon/OrderOpsPage';
import { FBAOpsPage } from './pages/amazon/FBAOpsPage';
import { PostSalesOpsPage } from './pages/amazon/PostSalesOpsPage';
import { BrandOpsPage } from './pages/amazon/BrandOpsPage';
import { CustomerGrowthPage } from './pages/amazon/CustomerGrowthPage';
import { PromotionOpsPage } from './pages/amazon/PromotionOpsPage';
import { BIOpsPage } from './pages/amazon/BIOpsPage';
import { FinanceOpsPage } from './pages/amazon/FinanceOpsPage';
import { RevenueOpsPage } from './pages/amazon/RevenueOpsPage';
import { PricingIntelPage } from './pages/amazon/PricingIntelPage';
import { CatalogDashboardPage } from './pages/amazon/CatalogDashboardPage';
import { MarketSyncProductsPage } from './pages/amazon/MarketSyncProductsPage';
import { MarketSyncInventoryPage } from './pages/amazon/MarketSyncInventoryPage';
import { MarketSyncOrdersPage } from './pages/amazon/MarketSyncOrdersPage';
import { MarketSyncOrderItemsPage } from './pages/amazon/MarketSyncOrderItemsPage';
import { SpapiV2OrdersPage } from './pages/amazon/SpapiV2OrdersPage';
import { SpapiV2OrderItemsPage } from './pages/amazon/SpapiV2OrderItemsPage';
import { MarketSyncFinancePage } from './pages/amazon/MarketSyncFinancePage';
import { MarketSyncPricingPage } from './pages/amazon/MarketSyncPricingPage';
import { CommercePlaceholderPage } from './pages/amazon/CommercePlaceholderPage';
import { AmazonDiscoveryPage } from './pages/amazon/AmazonDiscoveryPage';
import { ConnectDashboardPage } from './pages/amazon/ConnectDashboardPage';
import { SystemExplorerPage } from './pages/amazon/SystemExplorerPage';
import { AmazonApplicationSettingsPage } from './pages/amazon/AmazonApplicationSettingsPage';
import { AmazonOAuthCallbackPage } from './pages/amazon/AmazonOAuthCallbackPage';
import { MasterDataHubPage } from './master-data/MasterDataHubPage';
import { WarehouseMasterPage } from './master-data/WarehouseMasterPage';
import { InventoryDashboardPage } from './pages/amazon/InventoryDashboardPage';
import { OrdersDashboardPage } from './pages/amazon/OrdersDashboardPage';
import { ExperienceArchitecturePage } from './pages/platform/ExperienceArchitecturePage';
import { DesignSystemPage } from './pages/platform/DesignSystemPage';
import { AIXExperiencePage } from './pages/platform/AIXExperiencePage';
import { WorkspaceArchitecturePage } from './pages/platform/WorkspaceArchitecturePage';
import { HumanExperiencePage } from './pages/platform/HumanExperiencePage';
import { RoleExperiencePage } from './pages/platform/RoleExperiencePage';
import { AdaptiveExperiencePage } from './pages/platform/AdaptiveExperiencePage';
import { MotionExperiencePage } from './pages/platform/MotionExperiencePage';
import { IdentityPlatformPage } from './pages/platform/IdentityPlatformPage';
import { ExecutiveHomePage } from './pages/platform/ExecutiveHomePage';
import { MarketplaceConnectionPage } from './pages/platform/MarketplaceConnectionPage';
import { ProductIntelligencePage } from './pages/platform/ProductIntelligencePage';
import { ContentStudioPage } from './pages/platform/ContentStudioPage';
import { CreativeStudioPlatformPage } from './pages/platform/CreativeStudioPlatformPage';
import { CatalogOperationsPage } from './pages/platform/CatalogOperationsPage';
import { RevenueIntelligencePage } from './pages/platform/RevenueIntelligencePage';
import { SupplyChainPage } from './pages/platform/SupplyChainPage';
import { CustomerOperationsPage } from './pages/platform/CustomerOperationsPage';
import { FinancialIntelligencePage } from './pages/platform/FinancialIntelligencePage';
import { PlatformAdministrationPage } from './pages/platform/PlatformAdministrationPage';
import { ExecutiveIntelligencePage } from './pages/platform/ExecutiveIntelligencePage';
import { AutomationStudioPage } from './pages/platform/AutomationStudioPage';
import { KnowledgeCenterPage } from './pages/platform/KnowledgeCenterPage';
import { MobileCompanionPage } from './pages/platform/MobileCompanionPage';
import { UnifiedCommercePage } from './pages/platform/UnifiedCommercePage';
import { CustomerSuccessPage } from './pages/platform/CustomerSuccessPage';
import { EnterpriseIntegrationPage } from './pages/platform/EnterpriseIntegrationPage';
import { EnterpriseQualityPage } from './pages/platform/EnterpriseQualityPage';
import { EnterpriseDevOpsPage } from './pages/platform/EnterpriseDevOpsPage';
import { EnterpriseAcceptancePage } from './pages/platform/EnterpriseAcceptancePage';
import { EnterpriseBetaPage } from './pages/platform/EnterpriseBetaPage';
import { EnterpriseGAPage } from './pages/platform/EnterpriseGAPage';
import { MasterPlatformConstitutionPage } from './pages/platform/MasterPlatformConstitutionPage';
import { ArchitectureGovernancePage } from './pages/platform/ArchitectureGovernancePage';
import { EngineeringGovernancePage } from './pages/platform/EngineeringGovernancePage';
import { EnterpriseAIGovernancePage } from './pages/platform/EnterpriseAIGovernancePage';
import { SecurityGovernancePage } from './pages/platform/SecurityGovernancePage';
import { ProductGovernancePage } from './pages/platform/ProductGovernancePage';
import { DataGovernancePage } from './pages/platform/DataGovernancePage';
import { ReleaseGovernancePage } from './pages/platform/ReleaseGovernancePage';
import { MasterDevelopmentPlanPage } from './pages/platform/MasterDevelopmentPlanPage';
import { MasterDependencyMatrixPage } from './pages/platform/MasterDependencyMatrixPage';
import { CodingConstitutionPage } from './pages/platform/CodingConstitutionPage';
import { RepositoryBlueprintPage } from './pages/platform/RepositoryBlueprintPage';
import { DevEnvironmentBootstrapPage } from './pages/platform/DevEnvironmentBootstrapPage';

const MODULE_PAGES: Record<string, { title: string; description: string; module: string }> = {
  '/amazon/connect': {
    title: 'Amazon Seller Connection',
    description: 'Connect and manage Amazon seller accounts, OAuth flows, and marketplace registrations.',
    module: 'amazonconnect',
  },
  '/amazon/catalog': {
    title: 'Products',
    description: 'Founder Products workspace — MarketSync catalog SoT (search, sort, filter, paginate).',
    module: 'amazonconnect',
  },
  '/amazon/inventory': {
    title: 'Inventory',
    description: 'Founder Inventory workspace — MarketSync inventory SoT with channel quantities.',
    module: 'amazonconnect',
  },
  '/amazon/orders': {
    title: 'Orders',
    description: 'Founder Orders workspace — MarketSync order headers with realtime priority refresh.',
    module: 'amazonconnect',
  },
  '/amazon/order-items': {
    title: 'Order Items',
    description: 'Founder Order Items workspace — MarketSync order lines (getOrderItems) joining Orders, Products, Pricing and Finance.',
    module: 'amazonconnect',
  },
  '/amazon/spapi-v2/orders': {
    title: 'Sync V2 Orders',
    description: 'SP-API Sync V2 order headers — COD, Expedited, amounts from getOrders (Path B).',
    module: 'amazonconnect',
  },
  '/amazon/spapi-v2/order-items': {
    title: 'Sync V2 Order Items',
    description: 'SP-API Sync V2 order lines — ASIN and SKU from getOrderItems (Path B).',
    module: 'amazonconnect',
  },
  '/amazon/finance': {
    title: 'Finance',
    description: 'Founder Finance workspace — MarketSync financial events ledger.',
    module: 'amazonconnect',
  },
  '/amazon/pricing': {
    title: 'Pricing',
    description: 'Founder Pricing workspace — MarketSync offer price SoT.',
    module: 'amazonconnect',
  },
  '/amazon/discovery': {
    title: 'Amazon SP-API Discovery',
    description: 'Engineering knowledge catalog: probe SP-API/Reports capabilities, raw samples, field catalog (no business logic).',
    module: 'amazonconnect',
  },
  '/amazon/growth': {
    title: 'Amazon Growth Intelligence',
    description: 'AI-powered growth opportunities, recommendations, and governed execution for Amazon sellers.',
    module: 'amazongrowth',
  },
  '/amazon/advertising': {
    title: 'Amazon Advertising Intelligence',
    description: 'Campaign Copilot, bid & budget optimization, keyword intelligence, and governed ad execution.',
    module: 'amazonadsintel',
  },
  '/amazon/inventory-intel': {
    title: 'Enterprise Inventory Intelligence',
    description: 'Demand forecasting, replenishment planning, supplier health, scenario simulation, and AI inventory copilot.',
    module: 'inventoryintel',
  },
  '/amazon/pricing-intel': {
    title: 'Enterprise Pricing Intelligence',
    description: 'Profit optimization, pricing strategy, promotion planning, elasticity analysis, and AI commercial copilot.',
    module: 'pricingintel',
  },
  '/amazon/market-intel': {
    title: 'Enterprise Market Intelligence',
    description: 'Competitor analysis, category trends, opportunity discovery, trend detection, and AI market copilot.',
    module: 'marketintel',
  },
  '/amazon/voc-intel': {
    title: 'Voice of Customer Intelligence',
    description: 'Review intelligence, sentiment analysis, topic discovery, issue detection, return intelligence, and AI customer copilot.',
    module: 'vocintel',
  },
  '/amazon/content-intel': {
    title: 'Content Intelligence',
    description: 'Content optimization, A+ intelligence, brand store evaluation, image performance, experimentation, and AI content copilot.',
    module: 'contentintel',
  },
  '/planning/procurement': {
    title: 'Enterprise Procurement Planning',
    description: 'Purchase requirement planning, supplier intelligence, EOQ optimization, procurement risk detection, and explainable AI recommendations.',
    module: 'procurementplanning',
  },
  '/planning/supply': {
    title: 'Enterprise Supply Planning',
    description: 'Supply network planning, capacity optimization, constraint-based allocation, supply risk intelligence, and AI supply orchestration.',
    module: 'supplyplanning',
  },
  '/planning/warehouse-capacity': {
    title: 'Enterprise Warehouse Capacity Planning',
    description: 'Warehouse digital twin, capacity optimization, dock scheduling, labor planning, storage heatmaps, and fulfillment readiness.',
    module: 'warehousecapacityplanning',
  },
  '/planning/scenarios': {
    title: 'Enterprise Scenario Planning',
    description: 'Digital twin, cross-functional simulation, what-if analysis, risk modeling, constraint evaluation, and strategic decision intelligence.',
    module: 'scenarioplanning',
  },
  '/planning/financial': {
    title: 'Enterprise Financial Planning',
    description: 'FP&A, budgeting, cash flow planning, working capital optimization, profitability intelligence, and AI CFO recommendations.',
    module: 'financialplanning',
  },
  '/planning/sop': {
    title: 'Enterprise S&OP Platform',
    description: 'Integrated business planning governance, consensus planning, executive review, exception management, and AI S&OP intelligence.',
    module: 'sopplanning',
  },
  '/planning/analytics': {
    title: 'Enterprise Planning Analytics',
    description: 'Unified KPI registry, decision intelligence, executive scorecards, trend analysis, benchmarking, and cross-functional business intelligence.',
    module: 'planninganalytics',
  },
  '/planning/executive': {
    title: 'Executive Planning Cockpit',
    description: 'Strategic command center — executive decision intelligence, enterprise control tower, AI executive advisor, and governed approvals.',
    module: 'executivecockpit',
  },
  '/planning/decisions': {
    title: 'Enterprise Planning Decision Intelligence',
    description: 'Multi-agent reasoning engine — explainable recommendations, decision graph, governed approvals, learning feedback, and enterprise decision memory.',
    module: 'planningdecisionintel',
  },
  '/planning/autonomous': {
    title: 'Enterprise Autonomous Planning',
    description: 'Digital Employees, goal-driven planning automation, agent orchestration, human-supervised workflows, and execution readiness validation.',
    module: 'autonomousplanning',
  },
  '/planning/inventory': {
    title: 'Enterprise Inventory Planning',
    description: 'Safety stock optimization, replenishment intelligence, ABC-XYZ segmentation, multi-echelon planning, and explainable inventory recommendations.',
    module: 'inventoryplanning',
  },
  '/planning/ai-forecast': {
    title: 'AI Forecast Intelligence Engine',
    description: 'Multi-agent AI forecasting — explainable demand reasoning, prescriptive recommendations, scenario intelligence, and continuous learning above statistical forecasts.',
    module: 'aiforecast',
  },
  '/planning/forecasts': {
    title: 'Statistical & Sales Forecast Engine',
    description: 'Multi-algorithm statistical forecasting — SMA, Holt-Winters, Croston, accuracy management, and demand signal processing.',
    module: 'forecastengine',
  },
  '/planning/demand': {
    title: 'Enterprise Demand Planning',
    description: 'Authoritative demand plans — collaborative forecasting, hierarchy rollup, scenario comparison, adjustments, and approvals.',
    module: 'demandplanning',
  },
  '/planning': {
    title: 'Enterprise Planning Platform',
    description: 'Unified planning workspace — cycles, versions, scenarios, S&OP, approvals, and cross-domain planning coordination.',
    module: 'planning',
  },
  '/strategy/growth-planner': {
    title: 'Autonomous Growth Planner',
    description: 'Strategic planning, conflict resolution, scenario simulation, executive briefings, and cross-module recommendation consolidation.',
    module: 'growthplanner',
  },
  '/strategy/brand-portfolio': {
    title: 'Brand Portfolio Intelligence',
    description: 'Portfolio lifecycle management, innovation pipeline, cannibalization analysis, collection planning, and brand strategy recommendations.',
    module: 'brandportfolio',
  },
  '/amazon/search-intel': {
    title: 'Amazon Search Intelligence',
    description: 'Brand Analytics, Search Query Performance, keyword intelligence, organic rankings, search trends, and AI search copilot.',
    module: 'searchintel',
  },
  '/amazon/experiment-intel': {
    title: 'Amazon Experimentation Intelligence',
    description: 'Manage Your Experiments (MYE), AI experimentation copilot, statistical engine, variant management, and continuous learning.',
    module: 'expintel',
  },
  '/executive/daily-ops': {
    title: 'Daily Operations Center',
    description: 'Executive briefing, business health scores, priority engine, risk & opportunity centers, forecasts, and AI operations copilot.',
    module: 'dailyops',
  },
  '/operations/hub': {
    title: 'Operations Hub',
    description: 'Task intelligence, team collaboration, kanban board, workload management, AI task copilot, and operational execution.',
    module: 'opshub',
  },
  '/finance/intelligence': {
    title: 'Financial Intelligence',
    description: 'Cash flow planning, working capital, profitability analysis, scenario simulation, business health scores, and AI CFO recommendations.',
    module: 'finintel',
  },
  '/amazon/catalog-ops': {
    title: 'Catalog & Listing Operations',
    description: 'Catalog management, listing workspace, variation manager, bulk operations, listing health, compliance validation, and AI catalog copilot.',
    module: 'catalogops',
  },
  '/amazon/order-ops': {
    title: 'Orders & Fulfillment Operations',
    description: 'Order lifecycle management, shipment tracking, fulfillment intelligence, exception handling, AI order copilot, and operational monitoring.',
    module: 'orderops',
  },
  '/amazon/fba-ops': {
    title: 'FBA Inventory & Warehouse Intelligence',
    description: 'FBA inventory sync, inbound shipments, warehouse capacity, receiving reconciliation, restock planning, and AI warehouse copilot.',
    module: 'fbaops',
  },
  '/amazon/post-sales-ops': {
    title: 'Returns, SAFE-T & Post-Sales Intelligence',
    description: 'Return management, refund intelligence, SAFE-T claims, buyer messages, customer cases, reimbursements, quality intelligence, and AI customer copilot.',
    module: 'postsalesops',
  },
  '/amazon/brand-ops': {
    title: 'Brand Registry & Governance',
    description: 'Brand registry management, trademark protection, IP governance, compliance monitoring, counterfeit detection, brand health, and AI brand copilot.',
    module: 'brandops',
  },
  '/amazon/customer-growth': {
    title: 'Customer Growth & Engagement',
    description: 'Review management, Amazon Vine, Q&A, brand followers, engagement campaigns, customer intelligence, and AI customer growth copilot.',
    module: 'customergrowth',
  },
  '/amazon/promotion-ops': {
    title: 'Promotions & Commercial Campaigns',
    description: 'Coupons, lightning deals, event calendar, campaign planning, profitability analysis, scenario simulation, and AI commercial copilot.',
    module: 'promotionops',
  },
  '/amazon/bi-ops': {
    title: 'Business Intelligence & Executive Insights',
    description: 'Business reports, brand analytics, search performance, customer intelligence, executive insights, forecasting, and AI business copilot.',
    module: 'biops',
  },
  '/amazon/finance-ops': {
    title: 'Settlement & Finance Intelligence',
    description: 'Settlement management, financial events, fee analysis, reconciliation, leakage detection, cash flow forecasting, and AI financial copilot.',
    module: 'financeops',
  },
  '/amazon/revenue-ops': {
    title: 'Revenue Growth & Advertising Command Center',
    description: 'Campaign management, keyword intelligence, budget optimization, revenue planning, organic+paid correlation, forecasting, and AI revenue copilot.',
    module: 'revenueops',
  },
  '/platform/experience': {
    title: 'Enterprise Experience Architecture',
    description: 'EDP-00001 constitutional reference — navigation, IA, workspace standards, AI/human experience, and design governance.',
    module: 'experience',
  },
  '/platform/design-system': {
    title: 'Enterprise Design System',
    description: 'EDP-00002 visual language — design tokens, component library, AI components, motion, accessibility, and theme engine.',
    module: 'designsystem',
  },
  '/platform/aix': {
    title: 'Enterprise AI Experience',
    description: 'EDP-00003 copilot framework — AI roles, context engine, recommendations, simulations, approval standards, and human-AI collaboration.',
    module: 'aix',
  },
  '/platform/workspace': {
    title: 'Universal Workspace Architecture',
    description: 'EDP-00004 workspace shell — templates, layout panels, explorer, insights, timeline, toolbar standards, and responsive workspace guide.',
    module: 'workspaceshell',
  },
  '/platform/human-experience': {
    title: 'Enterprise Human Experience',
    description: 'EDP-00005 HX framework — user goals, journeys, task flows, decision design, onboarding milestones, cognitive load standards, and HX metrics.',
    module: 'hx',
  },
  '/platform/role-experience': {
    title: 'Role Experience Architecture',
    description: 'EDP-00006 role cockpits — executive dashboards, role KPIs, decision center, alert framework, executive briefings, and personalization standards.',
    module: 'rolexp',
  },
  '/platform/adaptive-experience': {
    title: 'Adaptive Experience Framework',
    description: 'EDP-00007 responsive architecture — device layouts, accessibility, personalization, cross-device continuity, and offline/degraded experience standards.',
    module: 'adaptivexp',
  },
  '/platform/motion-experience': {
    title: 'Motion & Emotional Experience Framework',
    description: 'EDP-00008 motion design — micro-interactions, feedback architecture, AI interaction states, loading/success/error experiences, and emotional experience standards.',
    module: 'motionxp',
  },
  '/platform/identity-platform': {
    title: 'Enterprise Identity Platform',
    description: 'UXP-00001 authentication, registration, organization onboarding, Amazon connection, security architecture, and identity governance standards.',
    module: 'identityxp',
  },
  '/platform/executive-home': {
    title: 'Executive Home Workspace',
    description: 'UXP-00002 business command center — daily AI briefing, health center, KPIs, opportunities, risks, decisions, tasks, and workspace health.',
    module: 'exechome',
  },
  '/platform/marketplace-connection': {
    title: 'Marketplace Connection Center',
    description: 'UXP-00003 Amazon SP-API OAuth, marketplace management, synchronization command center, AI brand initialization, and connection health.',
    module: 'marketconnect',
  },
  '/platform/product-intelligence': {
    title: 'Product Intelligence Workspace',
    description: 'UXP-00004 Product DNA engine, knowledge graph, health scoring, AI advisor, recommendations, and product optimization lifecycle.',
    module: 'productintel',
  },
  '/platform/content-studio': {
    title: 'AI Content Studio',
    description: 'UXP-00005 listing intelligence, SEO engine, AI writing studio, brand governance, compliance validation, and content publishing.',
    module: 'contentstudio',
  },
  '/platform/creative-studio': {
    title: 'AI Creative Studio',
    description: 'UXP-00006 visual commerce intelligence, AI Creative Director, Visual DNA engine, quality validation, and bulk creative factory.',
    module: 'creativestudio',
  },
  '/platform/catalog-operations': {
    title: 'Catalog Operations Platform',
    description: 'UXP-00007 product lifecycle, release management, version control, review & approval, simulation, publishing monitor, and rollback.',
    module: 'cataloglifecycle',
  },
  '/platform/revenue-intelligence': {
    title: 'Revenue Intelligence Platform',
    description: 'UXP-00008 AI advertising, revenue intelligence, campaign management, keyword intelligence, bid engine, forecasting, and AI optimization.',
    module: 'revenueintel',
  },
  '/platform/supply-chain': {
    title: 'Supply Chain Platform',
    description: 'UXP-00009 inventory intelligence, demand forecasting, replenishment, procurement, FBA operations, warehouse management, and cash flow intelligence.',
    module: 'supplychain',
  },
  '/platform/customer-operations': {
    title: 'Customer Operations Platform',
    description: 'UXP-00010 customer fulfillment, order intelligence, returns & refunds, customer communication, review intelligence, retention engine, and AI customer ops advisor.',
    module: 'customerops',
  },
  '/platform/financial-intelligence': {
    title: 'Financial Intelligence Platform',
    description: 'UXP-00011 settlement intelligence, profitability engine, GST compliance, TallyPrime integration, reconciliation, claims management, and AI CFO advisor.',
    module: 'financialintel',
  },
  '/platform/platform-administration': {
    title: 'Platform Administration',
    description: 'UXP-00012 enterprise administration, AI governance, tenant management, permission engine, integrations, security, compliance, and platform operations.',
    module: 'platformadmin',
  },
  '/platform/executive-intelligence': {
    title: 'Executive Intelligence Platform',
    description: 'UXP-00013 executive command center, AI briefings, unified analytics, forecasting, scenario simulation, decision intelligence, and AI executive copilot.',
    module: 'executiveintel',
  },
  '/platform/automation-studio': {
    title: 'AI Automation Studio',
    description: 'UXP-00014 AI workforce command center, agent library, agent builder, workflow designer, trigger engine, business rules, multi-agent collaboration, and human-in-the-loop.',
    module: 'automationstudio',
  },
  '/platform/knowledge-center': {
    title: 'AI Knowledge Center',
    description: 'UXP-00015 knowledge command center, knowledge base, learning academy, AI tutor, SOP management, knowledge graph, search engine, and documentation studio.',
    module: 'knowledgecenter',
  },
  '/platform/mobile-companion': {
    title: 'Mobile Companion',
    description: 'UXP-00016 executive mobile dashboard, AI briefings, notification center, executive approvals, AI companion, voice experience, crisis center, and offline experience.',
    module: 'mobilecompanion',
  },
  '/platform/unified-commerce': {
    title: 'Unified Commerce Platform',
    description: 'UXP-00017 commerce kernel, marketplace connectors, adapter framework, channel mapping, unified operations, and AI marketplace advisor.',
    module: 'unifiedcommerce',
  },
  '/platform/customer-success': {
    title: 'Customer Success Platform',
    description: 'UXP-00018 customer success command center, business onboarding, product adoption, AI success coach, success milestones, demo sandbox, and production readiness.',
    module: 'customersuccess',
  },
  '/platform/enterprise-integration': {
    title: 'Enterprise Integration Platform',
    description: 'LEP-00001 enterprise event bus, event catalog, service orchestration, message infrastructure, API webhook platform, AI orchestration, observability, and production wiring.',
    module: 'enterpriseintegration',
  },
  '/platform/integration-governance': {
    title: 'Integration Governance',
    description: 'DEV-000078 service registry, contract registry, dependency graph, integration health, version matrix, and cross-service governance.',
    module: 'enterpriseintegration',
  },
  '/platform/enterprise-quality': {
    title: 'Enterprise Quality Platform',
    description: 'LEP-00002 quality command center, test management, automated testing, AI validation, performance engineering, security validation, release certification, and continuous quality.',
    module: 'enterprisequality',
  },
  '/platform/enterprise-devops': {
    title: 'Enterprise DevOps Platform',
    description: 'LEP-00003 mission control, CI/CD pipelines, infrastructure, release management, observability, SRE, auto healing, and production operations.',
    module: 'enterprisedevops',
  },
  '/platform/enterprise-acceptance': {
    title: 'Enterprise Acceptance Platform',
    description: 'LEP-00004 alpha command center, internal tenant Nakabh, marketplace validation, feature acceptance, module acceptance, AI validation, UAT, and go-live readiness.',
    module: 'enterpriseacceptance',
  },
  '/platform/enterprise-beta': {
    title: 'Enterprise Beta Platform',
    description: 'LEP-00005 beta command center, customer enrollment, migration center, AI customer success manager, success tracking, commercial readiness, and beta operations.',
    module: 'enterprisebeta',
  },
  '/platform/enterprise-ga': {
    title: 'Enterprise GA Platform',
    description: 'LEP-00006 GA command center, commercial operations, customer lifecycle, revenue operations, customer success, product and release ops, marketplace and AI ops, and innovation platform.',
    module: 'enterprisega',
  },
  '/platform/master-constitution': {
    title: 'Master Platform Constitution',
    description: 'MPC-00001 supreme governance — core principles, canonical commerce kernel, AI constitution, security, quality, platform standards, and non-negotiable rules.',
    module: 'masterplatform',
  },
  '/platform/architecture-governance': {
    title: 'Enterprise Architecture Governance',
    description: 'EGB-00001 governance board, architecture principles, ADR governance, technology standards, domain and integration governance, and compliance evolution.',
    module: 'architecturegovernance',
  },
  '/platform/engineering-governance': {
    title: 'Engineering Governance',
    description: 'EGB-00002 engineering council, coding constitution, backend and frontend standards, AI engineering, git governance, code review, secure coding, testing, and CI gates.',
    module: 'engineeringgovernance',
  },
  '/platform/ai-governance': {
    title: 'Enterprise AI Governance',
    description: 'EGB-00003 AI governance council, responsible AI, lifecycle, model and prompt governance, DNA layers, agent governance, risk management, and compliance metrics.',
    module: 'enterpriseaigovernance',
  },
  '/platform/security-governance': {
    title: 'Enterprise Security Governance',
    description: 'EGB-00004 security council, Zero Trust, identity and access, secrets, privacy, marketplace security, AI security, incident response, and compliance dashboard.',
    module: 'securitygovernance',
  },
  '/platform/product-governance': {
    title: 'Enterprise Product Governance',
    description: 'EGB-00005 product council, vision, portfolio lifecycle, feature governance, roadmap, innovation, prioritization, and executive product dashboard.',
    module: 'productgovernance',
  },
  '/platform/data-governance': {
    title: 'Enterprise Data Governance',
    description: 'EGB-00006 data council, master data, metadata catalog, lineage, quality, lifecycle, privacy, AI data governance, and executive data dashboard.',
    module: 'datagovernance',
  },
  '/platform/release-governance': {
    title: 'Enterprise Release Governance',
    description: 'EGB-00007 CAB, change management, release readiness, configuration management, database and AI changes, rollback, and executive release dashboard.',
    module: 'releasegovernance',
  },
  '/platform/master-development-plan': {
    title: 'Master Development Execution Plan',
    description: 'IMP-00001 MDEP PMO team, execution principles, implementation phases 0-14, dependency and sprint management, DoR/DoD, quality gates, and executive dashboard.',
    module: 'masterdevelopment',
  },
  '/platform/master-dependency-matrix': {
    title: 'Master Development Dependency Matrix',
    description: 'IMP-00002 MDDM planning team, implementation layers 0-5, module dependencies, database/API/event chains, AI dependencies, parallel development, critical path, and executive dashboard.',
    module: 'masterdependency',
  },
  '/platform/coding-constitution': {
    title: 'Enterprise Coding Constitution',
    description: 'ICP-000001 technology lock, backend Go, frontend React, database/API/event, AI/marketplace, security/logging/error, testing/documentation/CI, DoR/DoD, and compliance dashboard.',
    module: 'codingconstitution',
  },
  '/platform/repository-blueprint': {
    title: 'Enterprise Repository Blueprint',
    description: 'ICP-000002 monorepo root structure, backend domains, frontend component library, PostgreSQL schemas, AI/marketplace folders, shared libraries, import/build rules, and ownership.',
    module: 'repositoryblueprint',
  },
  '/platform/dev-environment-bootstrap': {
    title: 'Development Environment Bootstrap',
    description: 'ICP-000003 P0 foundation — prerequisites, local Docker services, environment config, IDE tooling, bootstrap workflow, CI parity, and readiness gates.',
    module: 'devenvbootstrap',
  },
  '/amazon/sync': {
    title: 'Catalog Synchronization',
    description: 'Schedule and monitor Amazon sync jobs and data ingestion pipelines.',
    module: 'amazonsync',
  },
  '/ai/listing': {
    title: 'AI Listing Optimization',
    description: 'Analyze listings and apply AI-powered SEO and content optimizations.',
    module: 'aicatalog',
  },
  '/ai/creative': {
    title: 'Creative Studio',
    description: 'Generate product images, A+ content, and marketing creatives with AI.',
    module: 'aicreative',
  },
  '/integrations': {
    title: 'Integration Hub',
    description: 'Connect marketplaces, ERP, logistics, advertising, and custom APIs through a unified connector framework.',
    module: 'integration',
  },
  '/workflows': {
    title: 'Workflow Automation',
    description: 'Design, execute, and monitor cross-module automation workflows.',
    module: 'workflow',
  },
  '/publishing': {
    title: 'Release Management',
    description: 'Enterprise publishing, review, approval, scheduling, rollback & verification.',
    module: 'publishing',
  },
  '/analytics': {
    title: 'Analytics & BI',
    description: 'Enterprise business intelligence, executive dashboards, KPI engine, data warehouse and AI decision intelligence.',
    module: 'analytics',
  },
  '/copilot': {
    title: 'Growth Copilot',
    description: 'AI decision intelligence and growth recommendations.',
    module: 'copilot',
  },
  '/billing': {
    title: 'Billing & Subscriptions',
    description: 'Manage plans, subscriptions, credits, usage, and quotas.',
    module: 'commercial',
  },
  '/configuration': {
    title: 'Configuration Platform',
    description: 'Enterprise configuration, feature flags, tenant preferences, secrets and dynamic settings.',
    module: 'configuration',
  },
  '/events': {
    title: 'Event Platform',
    description: 'Enterprise event bus, message broker, domain events and cross-module communication.',
    module: 'eventplatform',
  },
  '/platform/event-mesh': {
    title: 'Enterprise Event Mesh',
    description: 'DEV-000079 event registry, schema governance, routing engine, replay capabilities and enterprise messaging.',
    module: 'eventplatform',
  },
  '/platform/api-gateway': {
    title: 'Enterprise API Gateway',
    description: 'DEV-000080 unified API ingress, OpenAPI registry, SDK generation, developer portal and API governance.',
    module: 'developer',
  },
  '/platform/persistence-governance': {
    title: 'Enterprise Persistence Platform',
    description: 'DEV-000081 repository framework, CQRS, transaction management, migration framework and multi-tenant data governance.',
    module: 'persistence',
  },
  '/platform/semantic-governance': {
    title: 'Enterprise Semantic Layer',
    description: 'DEV-000082 canonical business model, glossary, KPI registry, knowledge graph, metadata catalog and AI business context.',
    module: 'semantic',
  },
  '/platform/ai-operations': {
    title: 'Enterprise AI Platform',
    description: 'DEV-000083 prompt registry, model registry, agent registry, memory platform, tool registry, LLM routing and AI governance.',
    module: 'aiplatform',
  },
  '/platform/commercial-governance': {
    title: 'Enterprise Commercial Platform',
    description: 'DEV-000084 subscriptions, module licensing, feature flags, credit engine, usage metering, catalog and white label governance.',
    module: 'commercial',
  },
  '/platform/observability-governance': {
    title: 'Enterprise Observability Platform',
    description: 'DEV-000085 unified logging, metrics, distributed tracing, health monitoring, SLO management and operational analytics.',
    module: 'observability',
  },
  '/platform/foundation-certification': {
    title: 'Enterprise Foundation Certification',
    description: 'PRECHECK-000006 architecture audit, cross-platform validation, production readiness and enterprise foundation V2.0 certification.',
    module: 'commandcenter',
  },
  '/platform/product-family-constitution': {
    title: 'Enterprise Product Family Constitution',
    description: 'P00060 mandatory governance standard for every enterprise product family — modular, plug-and-play, AI-native and commercially licensable.',
    module: 'governance',
  },
  '/platform/engineering-execution-bible': {
    title: 'Engineering Execution Bible',
    description: 'EEB-000001 mandatory engineering constitution — coding standards, quality gates, performance budgets, definition of done and developer playbook.',
    module: 'governance',
  },
  '/platform/engineering-backlog-sprint-planning': {
    title: 'Engineering Backlog & Sprint Planning',
    description: 'EBP-000001 complete engineering backlog, epic breakdown, user stories, sprint planning, dependency mapping and production execution roadmap.',
    module: 'governance',
  },
  '/platform/architecture-decision-records': {
    title: 'Architecture Decision Records',
    description: 'ADR-000001 immutable technical decision register, engineering rationale, trade-off analysis and long-term architectural governance.',
    module: 'governance',
  },
  '/platform/repository-architecture-bible': {
    title: 'Repository Architecture Bible',
    description: 'RAB-000001 enterprise monorepo structure, package organization, module boundaries, dependency rules and long-term codebase governance.',
    module: 'governance',
  },
  '/platform/repository-bootstrap-production-foundation': {
    title: 'Repository Bootstrap & Production Foundation',
    description: 'DEV-000100 P0 blocker sprint — production engineering platform, monorepo skeleton, CI/CD, Docker, testing and developer tooling. No business logic.',
    module: 'governance',
  },
  '/platform/enterprise-identity-platform-dev101': {
    title: 'Enterprise Identity Platform',
    description: 'DEV-000101 P0 sprint — authentication, authorization, multi-tenant identity, RBAC, session management and enterprise access control.',
    module: 'governance',
  },
  '/platform/marketplace-integration-platform-dev102': {
    title: 'Marketplace Integration Platform',
    description: 'DEV-000102 P0 sprint — Amazon SP-API foundation, OAuth, marketplace connectivity, sync orchestration and integration framework.',
    module: 'governance',
  },
  '/platform/daily-growth-command-center-dev103': {
    title: 'Daily Growth Command Center',
    description: 'DEV-000103 P0 sprint — executive dashboard, opportunity inbox, AI decision center and default post-login workspace.',
    module: 'governance',
  },
  '/platform/product-workspace-asin360-dev104': {
    title: 'Product Workspace (ASIN 360)',
    description: 'DEV-000104 P0 sprint — unified product intelligence workspace, product operating system and enterprise product lifecycle.',
    module: 'governance',
  },
  '/platform/marketplace-dna-engine-dev105': {
    title: 'Marketplace DNA Engine',
    description: 'DEV-000105 P0 sprint — digital product twin, DNA scoring, versioning, benchmarks and intelligence foundation.',
    module: 'governance',
  },
  '/platform/listing-growth-intelligence-dev106': {
    title: 'Listing Growth Intelligence Engine',
    description: 'DEV-000106 P0 sprint — listing health, DNA-backed recommendations, simulation, version control and experiment integration.',
    module: 'governance',
  },
  '/platform/advertising-growth-intelligence-dev107': {
    title: 'Advertising Growth Intelligence Engine',
    description: 'DEV-000107 P0 sprint — campaign health, DNA-backed recommendations, bid/budget intelligence, simulation, attribution and experiment integration.',
    module: 'governance',
  },
  '/platform/fba-growth-intelligence-dev108': {
    title: 'FBA Growth Intelligence Engine',
    description: 'DEV-000108 P0 sprint — inventory health, demand forecasting, replenishment, shipment planning, scenario simulation and experiment integration.',
    module: 'governance',
  },
  '/platform/profit-intelligence-dev109': {
    title: 'Profit Intelligence & Leakage Detection Engine',
    description: 'DEV-000109 P0 sprint — true profit, leakage detection, settlement reconciliation, recovery management, cash flow and experiment integration.',
    module: 'governance',
  },
  '/platform/marketplace-operating-brain-dev110': {
    title: 'Marketplace Operating Brain',
    description: 'DEV-000110 P0 sprint — executive decision engine, recommendation orchestrator, evidence and reasoning, conversation, executive reports and knowledge graph.',
    module: 'governance',
  },
  '/platform/production-readiness-prt001': {
    title: 'Production Readiness Program',
    description: 'PRT-000001 P0 release blocker — end-to-end integration, quality gates, performance, security, scalability, operational readiness and pilot preparation.',
    module: 'governance',
  },
  '/platform/recommendation-validation-val001': {
    title: 'AI Recommendation Validation Framework',
    description: 'VAL-000001 P0 governance — scientific validation, experiment attribution, confidence evolution, pattern learning and evidence-driven intelligence.',
    module: 'governance',
  },
  '/platform/intelligence-qa-iqa001': {
    title: 'Intelligence Quality Assurance Framework',
    description: 'IQA-000001 P0 governance — cross-engine consistency, conflict detection, quality scoring, safety engine and decision approval pipeline.',
    module: 'governance',
  },
  '/platform/autonomous-ops-aow001': {
    title: 'Autonomous Operations & Workflow Engine',
    description: 'AOW-000001 P0 governance — task engine, workflow orchestration, approval framework, automation rules, escalation and execution history.',
    module: 'governance',
  },
  '/platform/ai-governance-gov001': {
    title: 'Enterprise AI Governance & Explainability',
    description: 'GOV-000001 P0 governance — decision traceability, explainability engine, evidence graph, counterfactual analysis, certification and audit trail.',
    module: 'governance',
  },
  '/platform/engineering-excellence-exl001': {
    title: 'Engineering Excellence & World-Class Standards',
    description: 'EXL-000001 P0 engineering standard — code quality, architecture compliance, technical debt, reliability, performance budgets, testing and release engineering.',
    module: 'governance',
  },
  '/platform/customer-value-cvd001': {
    title: 'Customer Value Dashboard',
    description: 'CVD-000001 P0 customer ROI — business value engine, revenue attribution, leakage recovery, renewal justification and executive reporting.',
    module: 'governance',
  },
  '/platform/experience-design-xdb001': {
    title: 'Experience Design Bible',
    description: 'XDB-000001 P0 experience standards — UX philosophy, visual design, motion, accessibility, cognitive load and design QA.',
    module: 'governance',
  },
  '/platform/screen-review-xdb002': {
    title: 'Screen-by-Screen Experience Review',
    description: 'XDB-000002 P0 screen audit — user journey review, workflow optimization, screen scoring and redesign plan.',
    module: 'governance',
  },
  '/platform/internal-alpha-alpha001': {
    title: 'Internal Alpha Operating & Validation',
    description: 'ALPHA-000001 P0 founder testing — daily operating procedures, workflow validation, defect discovery and exit criteria.',
    module: 'governance',
  },
  '/products/:asin/dna': {
    title: 'Marketplace DNA',
    description: 'DEV-000105 digital product twin — continuously evolving intelligence profile for every ASIN.',
    module: 'commandcenter',
  },
  '/products/:asin/workspace': {
    title: 'Product Workspace',
    description: 'DEV-000104 ASIN 360 — primary operating environment for every product with KPI ribbon, recommendations, timeline and AI panel.',
    module: 'commandcenter',
  },
  '/products/:asin/listing-growth': {
    title: 'Listing Growth Intelligence',
    description: 'DEV-000106 listing optimization — health scoring, DNA-backed recommendations, simulation, versions and experiments.',
    module: 'commandcenter',
  },
  '/advertising/:campaignId/growth': {
    title: 'Advertising Growth Intelligence',
    description: 'DEV-000107 advertising optimization — campaign health, DNA-backed recommendations, bid/budget intelligence, simulation, attribution and experiments.',
    module: 'commandcenter',
  },
  '/fba/:asin/growth': {
    title: 'FBA Growth Intelligence',
    description: 'DEV-000108 inventory optimization — health scoring, demand forecast, replenishment, shipment planning, scenarios and experiments.',
    module: 'commandcenter',
  },
  '/profit/:entityId/intelligence': {
    title: 'Profit Intelligence',
    description: 'DEV-000109 financial intelligence — true profit, leakage detection, settlement reconciliation, recovery, forecast, scenarios and DNA-backed recommendations.',
    module: 'commandcenter',
  },
  '/marketplace-brain': {
    title: 'Marketplace Operating Brain',
    description: 'DEV-000110 executive intelligence — cross-engine recommendations, morning brief, AI advisor, evidence, reasoning and knowledge graph.',
    module: 'commandcenter',
  },
  '/production-readiness': {
    title: 'Production Readiness',
    description: 'PRT-000001 readiness command center — integration validation, business journeys, reports, Go/No-Go, release checklist and risk register.',
    module: 'commandcenter',
  },
  '/recommendation-validation': {
    title: 'Recommendation Validation',
    description: 'VAL-000001 validation platform — outcome measurement, confidence evolution, pattern library, benchmarks, failure analysis and learning updates.',
    module: 'commandcenter',
  },
  '/intelligence-qa': {
    title: 'Intelligence QA',
    description: 'IQA-000001 QA command center — conflict detection, consolidation, quality scoring, safety assessment, decision governance and quality trends.',
    module: 'commandcenter',
  },
  '/autonomous-ops': {
    title: 'Autonomous Operations',
    description: 'AOW-000001 execution command center — tasks, workflows, approvals, escalations, automation rules, analytics and execution timeline.',
    module: 'commandcenter',
  },
  '/ai-governance': {
    title: 'AI Governance & Explainability',
    description: 'GOV-000001 trust command center — explainability, evidence graph, decision lineage, counterfactuals, certification, risk and audit trail.',
    module: 'commandcenter',
  },
  '/engineering-excellence': {
    title: 'Engineering Excellence',
    description: 'EXL-000001 excellence command center — code quality, architecture, tech debt, reliability, performance, observability, testing and release engineering.',
    module: 'commandcenter',
  },
  '/customer-value': {
    title: 'Customer Value Dashboard',
    description: 'CVD-000001 ROI command center — business value, revenue impact, profit attribution, leakage recovery, time saved, renewal center and executive reports.',
    module: 'commandcenter',
  },
  '/experience-design': {
    title: 'Experience Design',
    description: 'XDB-000001 experience command center — principles, visual design, accessibility, cognitive load, motion, design QA and usability metrics.',
    module: 'commandcenter',
  },
  '/screen-review': {
    title: 'Screen Review',
    description: 'XDB-000002 UX audit command center — screen catalog, scores, workflow friction, priority matrix and redesign plan.',
    module: 'commandcenter',
  },
  '/internal-alpha': {
    title: 'Internal Alpha',
    description: 'ALPHA-000001 founder alpha command center — daily checklist, workflow validation, defects, UX friction and exit criteria.',
    module: 'commandcenter',
  },
  '/dashboard': {
    title: 'Founder Dashboard',
    description: 'FOUNDER-DASHBOARD-IMPLEMENTATION-000001 — permanent understanding frame (chrome + widget slots; no KPI/chart data yet).',
    module: 'commandcenter',
  },
  '/platform/enterprise-design-system': {
    title: 'Enterprise Design System',
    description: 'EDS-000001 world-class UI, UX, visual language, component library, interaction system and marketplace operating experience.',
    module: 'governance',
  },
  '/platform/marketplace-intelligence-bible': {
    title: 'Marketplace Intelligence Bible',
    description: 'MIB-000001 vision, product philosophy, AI strategy, growth framework, north star metrics and marketplace operating model.',
    module: 'marketintel',
  },
  '/platform/marketplace-kpi-bible': {
    title: 'Marketplace KPI & Metrics Bible',
    description: 'MIB-000002 universal marketplace metrics, business formula dictionary, AI measurement framework and executive success indicators.',
    module: 'marketintel',
  },
  '/platform/marketplace-dna-bible': {
    title: 'Marketplace DNA & Knowledge Graph Bible',
    description: 'MIB-000003 product DNA engine, knowledge graph, AI context framework and continuous learning architecture.',
    module: 'marketintel',
  },
  '/platform/marketplace-growth-experiment-bible': {
    title: 'Marketplace Growth Experiment & Decision Intelligence Bible',
    description: 'MIB-000004 scientific optimization framework, recommendation engine, confidence scoring, attribution and continuous learning.',
    module: 'marketintel',
  },
  '/platform/marketplace-mos-bible': {
    title: 'Marketplace Operating System Bible',
    description: 'MIB-000005 world-class UX, human experience, daily workflow, navigation architecture and marketplace control center.',
    module: 'marketintel',
  },
  '/platform/marketplace-msi-bible': {
    title: 'Marketplace System Interaction & Workflow Architecture',
    description: 'MIB-000006 end-to-end workflow blueprint, cross-module interaction model, event propagation and product orchestration.',
    module: 'marketintel',
  },
  '/platform/marketplace-mvp-bible': {
    title: 'Marketplace Intelligence MVP & Release Roadmap',
    description: 'MIB-000007 feature prioritization, customer value matrix, release planning, commercial strategy and execution roadmap.',
    module: 'marketintel',
  },
  '/platform/marketplace-screen-bible': {
    title: 'Marketplace Screen Bible',
    description: 'UXS-000001 complete screen inventory, wireframes, layout specifications, user flows, component placement and screen-level engineering blueprint.',
    module: 'marketintel',
  },
  '/platform/marketplace-capability-architecture': {
    title: 'Marketplace Capability Architecture',
    description: 'MCA-000001 complete business capability map, product capability model, module boundaries, feature inventory, dependency matrix and engineering blueprint.',
    module: 'marketintel',
  },
  '/platform/marketplace-intelligence-v1-prd': {
    title: 'Marketplace Intelligence V1 PRD',
    description: 'PRD-000001 core platform product requirements — authentication, dashboard, DNA, search, navigation, audit, observability and all V1 features with full implementation specs.',
    module: 'marketintel',
  },
  '/platform/daily-growth-command-center-prd': {
    title: 'Daily Growth Command Center PRD',
    description: 'PRD-000002 marketplace OS home — executive workspace, AI command center, opportunity inbox, daily decision engine and proactive growth surface.',
    module: 'marketintel',
  },
  '/platform/product-workspace-asin360-prd': {
    title: 'Product Workspace ASIN 360 PRD',
    description: 'PRD-000003 unified product intelligence workspace — Marketplace DNA center, AI workspace, business performance hub and decision console.',
    module: 'marketintel',
  },
  '/platform/listing-growth-engine-prd': {
    title: 'Listing Growth Engine PRD',
    description: 'PRD-000004 AI listing intelligence, SEO optimization, content intelligence, visual intelligence, listing health, attribution and continuous optimization.',
    module: 'marketintel',
  },
  '/platform/advertising-growth-engine-prd': {
    title: 'Advertising Growth Engine PRD',
    description: 'PRD-000005 AI advertising intelligence, campaign optimization, search term intelligence, bid intelligence, budget intelligence, attribution and profit optimization.',
    module: 'marketintel',
  },
  '/platform/fba-growth-engine-prd': {
    title: 'FBA Growth Engine PRD',
    description: 'PRD-000006 inventory intelligence, replenishment intelligence, shipment intelligence, capacity planning, inventory health, forecasting and cost optimization.',
    module: 'marketintel',
  },
  '/platform/profit-intelligence-leakage-engine-prd': {
    title: 'Profit Intelligence & Leakage Detection PRD',
    description: 'PRD-000007 true profitability, financial intelligence, marketplace reconciliation, leakage detection, cash flow intelligence and attribution.',
    module: 'marketintel',
  },
  '/platform/growth-experiment-engine-prd': {
    title: 'Growth Experiment & Continuous Learning PRD',
    description: 'PRD-000008 scientific optimization, attribution intelligence, experiment orchestration, AI learning framework and knowledge evolution.',
    module: 'marketintel',
  },
  '/platform/marketplace-operating-brain-prd': {
    title: 'Marketplace Operating Brain PRD',
    description: 'PRD-000009 autonomous marketplace intelligence, business decision engine, AI orchestration platform and executive advisor.',
    module: 'marketintel',
  },
  '/plugins': {
    title: 'Plugin Platform',
    description: 'Enterprise plugin SDK, extension framework, developer platform and marketplace ecosystem.',
    module: 'pluginplatform',
  },
  '/automation': {
    title: 'Automation Platform',
    description: 'Enterprise AI automation, workflow engine, business rules and agent orchestration.',
    module: 'automation',
  },
  '/communication': {
    title: 'Communication Platform',
    description: 'Enterprise omnichannel communication, notification, messaging and campaign engine.',
    module: 'communication',
  },
  '/documents': {
    title: 'Document Platform',
    description: 'Enterprise document management, OCR, digital signature and compliance platform.',
    module: 'documents',
  },
  '/developer': {
    title: 'Developer Portal',
    description: 'Public APIs, OAuth applications, webhooks, SDKs, extensions, and app marketplace.',
    module: 'developer',
  },
  '/knowledge': {
    title: 'Knowledge Platform',
    description: 'Enterprise knowledge graph, semantic search, AI memory, and RAG context assembly.',
    module: 'knowledge',
  },
  '/governance': {
    title: 'Governance & Trust',
    description: 'Policies, compliance, privacy, audit, risk management, and trust center.',
    module: 'governance',
  },
  '/sre': {
    title: 'Platform Operations & SRE',
    description: 'High availability, disaster recovery, deployments, backups, incidents, and capacity planning.',
    module: 'sre',
  },
  '/command': {
    title: 'Command Center',
    description:
      'Founder Decision Board — what should I do next? Certified LIVE actions only (COMMAND-CENTER-000001).',
    module: 'commandcenter',
  },
  '/admin': {
    title: 'Administration Center',
    description: 'Platform configuration, feature flags, governance, and audit.',
    module: 'administration',
  },
  '/saas': {
    title: 'Global SaaS Dashboard',
    description: 'Enterprise SaaS administration, tenant operations, organization management and white-label platform.',
    module: 'saas',
  },
  '/decision': {
    title: 'Executive Decision Center',
    description: 'Enterprise AI decision engine, autonomous commerce agents, strategic optimization and autonomous business operations.',
    module: 'decision',
  },
  '/resilience': {
    title: 'Enterprise Resilience Platform',
    description: 'Backup, disaster recovery, high availability, business continuity and platform resilience.',
    module: 'resilience',
  },
  '/security': {
    title: 'Enterprise Security Operations Center',
    description: 'Zero Trust security, identity governance, threat detection, compliance and SOC operations.',
    module: 'security',
  },
  '/optimization': {
    title: 'Autonomous Platform Optimization',
    description: 'Self-healing infrastructure, cost intelligence, capacity management and continuous improvement.',
    module: 'optimization',
  },
  '/observability': {
    title: 'Operations Center',
    description: 'Enterprise observability, monitoring, telemetry, distributed tracing, SRE and incident management.',
    module: 'observability',
  },
};

export default function App() {
  const [nav, setNav] = useState<NavItem[]>(FALLBACK_NAV);

  useEffect(() => {
    alphaApi.navigation().then(setNav).catch(() => setNav(FALLBACK_NAV));
  }, []);

  return (
    <AppShell nav={nav}>
          <Routes>
            <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
            <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
            <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
            <Route path="/reset-password" element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />
            <Route path="/verify-email" element={<GuestRoute><VerifyEmailPage /></GuestRoute>} />
            {/* Public Amazon OAuth callback — must not require login or OAuth query params are lost */}
            <Route path="/amazon/connect/callback" element={<AmazonOAuthCallbackPage />} />
            <Route path="/account/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/account/security" element={<ProtectedRoute><SecurityDashboardPage /></ProtectedRoute>} />
            <Route path="/account/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
            <Route path="/account/sessions" element={<ProtectedRoute><ActiveSessionsPage /></ProtectedRoute>} />
            <Route path="/account/trusted-devices" element={<ProtectedRoute><TrustedDevicesPage /></ProtectedRoute>} />
            <Route path="/authorization" element={<ProtectedRoute><AuthorizationDashboardPage /></ProtectedRoute>} />
            <Route path="/authorization/roles" element={<ProtectedRoute><RolesPage /></ProtectedRoute>} />
            <Route path="/authorization/permissions" element={<ProtectedRoute><PermissionsPage /></ProtectedRoute>} />
            <Route path="/authorization/policies" element={<ProtectedRoute><PoliciesPage /></ProtectedRoute>} />
            <Route path="/authorization/policies/builder" element={<ProtectedRoute><PolicyBuilderPage /></ProtectedRoute>} />
            <Route path="/authorization/matrix" element={<ProtectedRoute><PermissionMatrixPage /></ProtectedRoute>} />
            <Route path="/authorization/assignments" element={<ProtectedRoute><RoleAssignmentPage /></ProtectedRoute>} />
            <Route path="/authorization/audit" element={<ProtectedRoute><PermissionAuditPage /></ProtectedRoute>} />
            <Route path="/authorization/me" element={<ProtectedRoute><UserPermissionsPage /></ProtectedRoute>} />
            <Route path="/organizations" element={<ProtectedRoute><OrganizationListPage /></ProtectedRoute>} />
            <Route path="/organizations/search" element={<ProtectedRoute><OrganizationSearchPage /></ProtectedRoute>} />
            <Route path="/organizations/new" element={<ProtectedRoute><OrganizationCreatePage /></ProtectedRoute>} />
            <Route path="/organizations/:id/dashboard" element={<ProtectedRoute><OrganizationDashboardPage /></ProtectedRoute>} />
            <Route path="/organizations/:id" element={<ProtectedRoute><OrganizationDetailPage /></ProtectedRoute>} />
            <Route path="/tenants" element={<ProtectedRoute><TenantListPage /></ProtectedRoute>} />
            <Route path="/tenants/search" element={<ProtectedRoute><TenantSearchPage /></ProtectedRoute>} />
            <Route path="/tenants/new" element={<ProtectedRoute><TenantCreatePage /></ProtectedRoute>} />
            <Route path="/tenants/:id/dashboard" element={<ProtectedRoute><TenantDashboardPage /></ProtectedRoute>} />
            <Route path="/tenants/:id" element={<ProtectedRoute><TenantDetailPage /></ProtectedRoute>} />
            <Route path="/workspaces" element={<ProtectedRoute><WorkspaceListPage /></ProtectedRoute>} />
            <Route path="/workspaces/search" element={<ProtectedRoute><WorkspaceSearchPage /></ProtectedRoute>} />
            <Route path="/workspaces/new" element={<ProtectedRoute><WorkspaceCreatePage /></ProtectedRoute>} />
            <Route path="/workspaces/:id/dashboard" element={<ProtectedRoute><WorkspaceDashboardPage /></ProtectedRoute>} />
            <Route path="/workspaces/:id/analytics" element={<ProtectedRoute><WorkspaceAnalyticsPage /></ProtectedRoute>} />
            <Route path="/workspaces/:id" element={<ProtectedRoute><WorkspaceDetailPage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UserDirectoryPage /></ProtectedRoute>} />
            <Route path="/users/new" element={<ProtectedRoute><UserCreatePage /></ProtectedRoute>} />
            <Route path="/users/invitations" element={<ProtectedRoute><InvitationsPage /></ProtectedRoute>} />
            <Route path="/users/:id" element={<ProtectedRoute><UserDetailPage /></ProtectedRoute>} />
            <Route path="/identity" element={<ProtectedRoute><IdentityDirectoryPage /></ProtectedRoute>} />
            <Route path="/identity/search" element={<ProtectedRoute><IdentitySearchPage /></ProtectedRoute>} />
            <Route path="/identity/:id" element={<ProtectedRoute><IdentityDetailsPage /></ProtectedRoute>} />
            <Route path="/identity/:id/profile" element={<ProtectedRoute><IdentityProfilePage /></ProtectedRoute>} />
            <Route path="/identity/:id/preferences" element={<ProtectedRoute><IdentityPreferencesPage /></ProtectedRoute>} />
            <Route path="/identity/:id/activity" element={<ProtectedRoute><IdentityActivityPage /></ProtectedRoute>} />
            <Route path="/identity/:id/audit" element={<ProtectedRoute><IdentityAuditPage /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><ProductListPage /></ProtectedRoute>} />
            <Route path="/products/dashboard" element={<ProtectedRoute><ProductDashboardPage /></ProtectedRoute>} />
            <Route path="/products/search" element={<ProtectedRoute><ProductSearchPage /></ProtectedRoute>} />
            <Route path="/products/new" element={<ProtectedRoute><ProductEditorPage /></ProtectedRoute>} />
            <Route path="/products/:id/edit" element={<ProtectedRoute><ProductEditorPage /></ProtectedRoute>} />
            <Route path="/products/:id/approval" element={<ProtectedRoute><ProductApprovalPage /></ProtectedRoute>} />
            <Route path="/products/:id/health" element={<ProtectedRoute><ProductHealthPage /></ProtectedRoute>} />
            <Route path="/products/:id/relationships" element={<ProtectedRoute><ProductRelationshipsPage /></ProtectedRoute>} />
            <Route path="/products/:asin/workspace" element={<ProtectedRoute><ProductWorkspacePage /></ProtectedRoute>} />
            <Route path="/products/:asin/dna" element={<ProtectedRoute><MarketplaceDNAEnginePage /></ProtectedRoute>} />
            <Route path="/products/:asin/listing-growth" element={<ProtectedRoute><ListingGrowthIntelligencePage /></ProtectedRoute>} />
            <Route path="/advertising/:campaignId/growth" element={<ProtectedRoute><AdvertisingGrowthIntelligencePage /></ProtectedRoute>} />
            <Route path="/fba/:asin/growth" element={<ProtectedRoute><FBAGrowthIntelligencePage /></ProtectedRoute>} />
            <Route path="/profit/:entityId/intelligence" element={<ProtectedRoute><ProfitIntelligencePage /></ProtectedRoute>} />
            <Route path="/marketplace-brain" element={<ProtectedRoute><MarketplaceOperatingBrainPage /></ProtectedRoute>} />
            <Route path="/production-readiness" element={<ProtectedRoute><ProductionReadinessPage /></ProtectedRoute>} />
            <Route path="/recommendation-validation" element={<ProtectedRoute><RecommendationValidationPage /></ProtectedRoute>} />
            <Route path="/intelligence-qa" element={<ProtectedRoute><IntelligenceQAPage /></ProtectedRoute>} />
            <Route path="/autonomous-ops" element={<ProtectedRoute><AutonomousOperationsPage /></ProtectedRoute>} />
            <Route path="/ai-governance" element={<ProtectedRoute><AIGovernancePage /></ProtectedRoute>} />
            <Route path="/engineering-excellence" element={<ProtectedRoute><EngineeringExcellencePage /></ProtectedRoute>} />
            <Route path="/customer-value" element={<ProtectedRoute><CustomerValuePage /></ProtectedRoute>} />
            <Route path="/experience-design" element={<ProtectedRoute><ExperienceDesignPage /></ProtectedRoute>} />
            <Route path="/screen-review" element={<ProtectedRoute><ScreenReviewPage /></ProtectedRoute>} />
            <Route path="/internal-alpha" element={<ProtectedRoute><InternalAlphaPage /></ProtectedRoute>} />
            <Route path="/products/:id" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
            <Route path="/media" element={<ProtectedRoute><AssetLibraryPage /></ProtectedRoute>} />
            <Route path="/media/search" element={<ProtectedRoute><AssetSearchPage /></ProtectedRoute>} />
            <Route path="/media/bulk-upload" element={<ProtectedRoute><BulkUploadPage /></ProtectedRoute>} />
            <Route path="/media/approval" element={<ProtectedRoute><ApprovalQueuePage /></ProtectedRoute>} />
            <Route path="/media/duplicates" element={<ProtectedRoute><DuplicateManagerPage /></ProtectedRoute>} />
            <Route path="/media/prompts" element={<ProtectedRoute><PromptLibraryPage /></ProtectedRoute>} />
            <Route path="/media/prompts/new" element={<ProtectedRoute><PromptEditorPage /></ProtectedRoute>} />
            <Route path="/media/prompts/:id/edit" element={<ProtectedRoute><PromptEditorPage /></ProtectedRoute>} />
            <Route path="/media/generations" element={<ProtectedRoute><GenerationHistoryPage /></ProtectedRoute>} />
            <Route path="/media/quality/:id" element={<ProtectedRoute><QualityDashboardPage /></ProtectedRoute>} />
            <Route path="/media/assets/:id" element={<ProtectedRoute><AssetDetailPage /></ProtectedRoute>} />
            <Route path="/catalog" element={<ProtectedRoute><CatalogListPage /></ProtectedRoute>} />
            <Route path="/catalog/dashboard" element={<ProtectedRoute><EnterpriseCatalogDashboardPage /></ProtectedRoute>} />
            <Route path="/catalog/categories/tree" element={<ProtectedRoute><CategoryTreePage /></ProtectedRoute>} />
            <Route path="/catalog/categories/new" element={<ProtectedRoute><CategoryEditorPage /></ProtectedRoute>} />
            <Route path="/catalog/categories/:id/edit" element={<ProtectedRoute><CategoryEditorPage /></ProtectedRoute>} />
            <Route path="/catalog/marketplace-mapping" element={<ProtectedRoute><MarketplaceMappingPage /></ProtectedRoute>} />
            <Route path="/catalog/collections" element={<ProtectedRoute><CollectionBuilderPage /></ProtectedRoute>} />
            <Route path="/catalog/collections/smart" element={<ProtectedRoute><SmartCollectionBuilderPage /></ProtectedRoute>} />
            <Route path="/catalog/classification" element={<ProtectedRoute><ClassificationDashboardPage /></ProtectedRoute>} />
            <Route path="/catalog/classification/review" element={<ProtectedRoute><AIClassificationReviewPage /></ProtectedRoute>} />
            <Route path="/catalog/seo" element={<ProtectedRoute><SEOCategoriesPage /></ProtectedRoute>} />
            <Route path="/catalog/taxonomy" element={<ProtectedRoute><TaxonomyExplorerPage /></ProtectedRoute>} />
            <Route path="/catalog/hsn" element={<ProtectedRoute><HSNMappingPage /></ProtectedRoute>} />
            <Route path="/catalog/gst" element={<ProtectedRoute><GSTClassificationPage /></ProtectedRoute>} />
            <Route path="/product-structure" element={<ProtectedRoute><ProductStructureHubPage /></ProtectedRoute>} />
            <Route path="/product-structure/variants" element={<ProtectedRoute><VariantManagerPage /></ProtectedRoute>} />
            <Route path="/product-structure/variant-matrix" element={<ProtectedRoute><VariantMatrixPage /></ProtectedRoute>} />
            <Route path="/product-structure/bundles" element={<ProtectedRoute><BundleBuilderPage /></ProtectedRoute>} />
            <Route path="/product-structure/kits" element={<ProtectedRoute><KitBuilderPage /></ProtectedRoute>} />
            <Route path="/product-structure/relationships" element={<ProtectedRoute><RelationshipManagerPage /></ProtectedRoute>} />
            <Route path="/product-structure/families" element={<ProtectedRoute><ProductFamilyPage /></ProtectedRoute>} />
            <Route path="/product-structure/marketplace" element={<ProtectedRoute><MarketplaceStructurePage /></ProtectedRoute>} />
            <Route path="/product-structure/dependencies" element={<ProtectedRoute><DependencyGraphPage /></ProtectedRoute>} />
            <Route path="/product-attributes" element={<ProtectedRoute><ProductAttributesHubPage /></ProtectedRoute>} />
            <Route path="/product-attributes/library" element={<ProtectedRoute><AttributeLibraryPage /></ProtectedRoute>} />
            <Route path="/product-attributes/builder" element={<ProtectedRoute><AttributeBuilderPage /></ProtectedRoute>} />
            <Route path="/product-attributes/sets" element={<ProtectedRoute><AttributeSetsPage /></ProtectedRoute>} />
            <Route path="/product-attributes/templates" element={<ProtectedRoute><TemplateManagerPage /></ProtectedRoute>} />
            <Route path="/product-attributes/specifications" element={<ProtectedRoute><SpecificationBuilderPage /></ProtectedRoute>} />
            <Route path="/product-attributes/marketplace" element={<ProtectedRoute><MarketplaceAttributeMappingPage /></ProtectedRoute>} />
            <Route path="/product-attributes/validation" element={<ProtectedRoute><ValidationDashboardPage /></ProtectedRoute>} />
            <Route path="/product-attributes/recommendations" element={<ProtectedRoute><AIRecommendationsPage /></ProtectedRoute>} />
            <Route path="/product-attributes/localization" element={<ProtectedRoute><LocalizationPage /></ProtectedRoute>} />
            <Route path="/product-attributes/units" element={<ProtectedRoute><UnitsOfMeasurePage /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><PricingHubPage /></ProtectedRoute>} />
            <Route path="/pricing/price-books" element={<ProtectedRoute><PriceBookManagerPage /></ProtectedRoute>} />
            <Route path="/pricing/marketplace" element={<ProtectedRoute><MarketplacePricingPage /></ProtectedRoute>} />
            <Route path="/pricing/promotions" element={<ProtectedRoute><PromotionManagerPage /></ProtectedRoute>} />
            <Route path="/pricing/discounts" element={<ProtectedRoute><DiscountManagerPage /></ProtectedRoute>} />
            <Route path="/pricing/coupons" element={<ProtectedRoute><CouponManagerPage /></ProtectedRoute>} />
            <Route path="/pricing/bulk-update" element={<ProtectedRoute><BulkPriceUpdatePage /></ProtectedRoute>} />
            <Route path="/pricing/analytics" element={<ProtectedRoute><PricingAnalyticsPage /></ProtectedRoute>} />
            <Route path="/pricing/margin" element={<ProtectedRoute><MarginDashboardPage /></ProtectedRoute>} />
            <Route path="/pricing/recommendations" element={<ProtectedRoute><AIPricingRecommendationsPage /></ProtectedRoute>} />
            <Route path="/pricing/competitors" element={<ProtectedRoute><CompetitorPricingPage /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><InventoryHubPage /></ProtectedRoute>} />
            <Route path="/inventory/warehouses" element={<ProtectedRoute><IMSWarehouseDashboardPage /></ProtectedRoute>} />
            <Route path="/inventory/warehouse-manager" element={<ProtectedRoute><WarehouseManagerPage /></ProtectedRoute>} />
            <Route path="/inventory/stock" element={<ProtectedRoute><IMSInventoryDashboardPage /></ProtectedRoute>} />
            <Route path="/inventory/ledger" element={<ProtectedRoute><InventoryLedgerPage /></ProtectedRoute>} />
            <Route path="/inventory/transfers" element={<ProtectedRoute><StockTransferPage /></ProtectedRoute>} />
            <Route path="/inventory/reservations" element={<ProtectedRoute><ReservationManagerPage /></ProtectedRoute>} />
            <Route path="/inventory/allocations" element={<ProtectedRoute><AllocationManagerPage /></ProtectedRoute>} />
            <Route path="/inventory/cycle-counts" element={<ProtectedRoute><CycleCountPage /></ProtectedRoute>} />
            <Route path="/inventory/physical-verification" element={<ProtectedRoute><PhysicalVerificationPage /></ProtectedRoute>} />
            <Route path="/inventory/forecast" element={<ProtectedRoute><InventoryForecastPage /></ProtectedRoute>} />
            <Route path="/inventory/health" element={<ProtectedRoute><InventoryHealthPage /></ProtectedRoute>} />
            <Route path="/inventory/marketplace" element={<ProtectedRoute><IMSMarketplaceInventoryPage /></ProtectedRoute>} />
            <Route path="/listings" element={<ProtectedRoute><ListingsHubPage /></ProtectedRoute>} />
            <Route path="/listings/manager" element={<ProtectedRoute><ListingManagerPage /></ProtectedRoute>} />
            <Route path="/listings/editor" element={<ProtectedRoute><ListingEditorPage /></ProtectedRoute>} />
            <Route path="/listings/preview" element={<ProtectedRoute><MarketplacePreviewPage /></ProtectedRoute>} />
            <Route path="/listings/health" element={<ProtectedRoute><ListingHealthDashboardPage /></ProtectedRoute>} />
            <Route path="/listings/seo" element={<ProtectedRoute><SEODashboardPage /></ProtectedRoute>} />
            <Route path="/listings/compliance" element={<ProtectedRoute><ComplianceDashboardPage /></ProtectedRoute>} />
            <Route path="/listings/bulk" element={<ProtectedRoute><BulkListingGeneratorPage /></ProtectedRoute>} />
            <Route path="/listings/compare" element={<ProtectedRoute><VersionComparisonPage /></ProtectedRoute>} />
            <Route path="/listings/templates" element={<ProtectedRoute><MarketplaceTemplatesPage /></ProtectedRoute>} />
            <Route path="/listings/recommendations" element={<ProtectedRoute><AIRecommendationsPanelPage /></ProtectedRoute>} />
            <Route path="/marketplace-sync" element={<ProtectedRoute><MarketplaceSyncHubPage /></ProtectedRoute>} />
            <Route path="/marketplace-sync/publications" element={<ProtectedRoute><PublicationQueuePage /></ProtectedRoute>} />
            <Route path="/marketplace-sync/feeds" element={<ProtectedRoute><FeedMonitorPage /></ProtectedRoute>} />
            <Route path="/marketplace-sync/sync" element={<ProtectedRoute><SyncDashboardPage /></ProtectedRoute>} />
            <Route path="/marketplace-sync/retry" element={<ProtectedRoute><RetryQueuePage /></ProtectedRoute>} />
            <Route path="/marketplace-sync/dead-letter" element={<ProtectedRoute><DeadLetterQueuePage /></ProtectedRoute>} />
            <Route path="/marketplace-sync/connectors" element={<ProtectedRoute><ConnectorManagerPage /></ProtectedRoute>} />
            <Route path="/marketplace-sync/webhooks" element={<ProtectedRoute><WebhookMonitorPage /></ProtectedRoute>} />
            <Route path="/marketplace-accounts" element={<ProtectedRoute><MarketplaceAccountHubPage /></ProtectedRoute>} />
            <Route path="/marketplace-accounts/wizard" element={<ProtectedRoute><ConnectionWizardPage /></ProtectedRoute>} />
            <Route path="/marketplace-accounts/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
            <Route path="/marketplace-accounts/credentials" element={<ProtectedRoute><CredentialManagerPage /></ProtectedRoute>} />
            <Route path="/marketplace-accounts/health" element={<ProtectedRoute><HealthDashboardPage /></ProtectedRoute>} />
            <Route path="/marketplace-accounts/settings" element={<ProtectedRoute><ChannelSettingsPage /></ProtectedRoute>} />
            <Route path="/marketplace-accounts/mappings" element={<ProtectedRoute><MappingsPage /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrderHubPage /></ProtectedRoute>} />
            <Route path="/orders/list" element={<ProtectedRoute><OrderListPage /></ProtectedRoute>} />
            <Route path="/orders/import" element={<ProtectedRoute><OrderImportPage /></ProtectedRoute>} />
            <Route path="/orders/allocation" element={<ProtectedRoute><AllocationDashboardPage /></ProtectedRoute>} />
            <Route path="/orders/analytics" element={<ProtectedRoute><OrderAnalyticsPage /></ProtectedRoute>} />
            <Route path="/orders/sla" element={<ProtectedRoute><SLADashboardPage /></ProtectedRoute>} />
            <Route path="/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
            <Route path="/warehouse" element={<ProtectedRoute><WarehouseHubPage /></ProtectedRoute>} />
            <Route path="/warehouse/picking" element={<ProtectedRoute><PickingDashboardPage /></ProtectedRoute>} />
            <Route path="/warehouse/packing" element={<ProtectedRoute><PackingDashboardPage /></ProtectedRoute>} />
            <Route path="/warehouse/dispatch" element={<ProtectedRoute><DispatchDashboardPage /></ProtectedRoute>} />
            <Route path="/warehouse/docks" element={<ProtectedRoute><DockDashboardPage /></ProtectedRoute>} />
            <Route path="/warehouse/workers" element={<ProtectedRoute><WorkerDashboardPage /></ProtectedRoute>} />
            <Route path="/warehouse/waves" element={<ProtectedRoute><WavePlannerPage /></ProtectedRoute>} />
            <Route path="/warehouse/kpis" element={<ProtectedRoute><WarehouseKPIPage /></ProtectedRoute>} />
            <Route path="/warehouse/mobile" element={<ProtectedRoute><MobileWarehousePage /></ProtectedRoute>} />
            <Route path="/logistics" element={<ProtectedRoute><LogisticsHubPage /></ProtectedRoute>} />
            <Route path="/logistics/shipments" element={<ProtectedRoute><ShipmentsPage /></ProtectedRoute>} />
            <Route path="/logistics/carriers" element={<ProtectedRoute><CarriersPage /></ProtectedRoute>} />
            <Route path="/logistics/tracking" element={<ProtectedRoute><TrackingPage /></ProtectedRoute>} />
            <Route path="/logistics/rates" element={<ProtectedRoute><RateComparisonPage /></ProtectedRoute>} />
            <Route path="/logistics/serviceability" element={<ProtectedRoute><ServiceabilityPage /></ProtectedRoute>} />
            <Route path="/logistics/analytics" element={<ProtectedRoute><LogisticsAnalyticsPage /></ProtectedRoute>} />
            <Route path="/returns" element={<ProtectedRoute><ReturnsHubPage /></ProtectedRoute>} />
            <Route path="/returns/list" element={<ProtectedRoute><ReturnsListPage /></ProtectedRoute>} />
            <Route path="/returns/rma" element={<ProtectedRoute><RMADashboardPage /></ProtectedRoute>} />
            <Route path="/returns/refunds" element={<ProtectedRoute><RefundDashboardPage /></ProtectedRoute>} />
            <Route path="/returns/reverse" element={<ProtectedRoute><ReverseLogisticsPage /></ProtectedRoute>} />
            <Route path="/returns/claims" element={<ProtectedRoute><ClaimsDashboardPage /></ProtectedRoute>} />
            <Route path="/returns/rto" element={<ProtectedRoute><RTODashboardPage /></ProtectedRoute>} />
            <Route path="/returns/analytics" element={<ProtectedRoute><ReturnsAnalyticsPage /></ProtectedRoute>} />
            <Route path="/finance" element={<ProtectedRoute><FinanceHubPage /></ProtectedRoute>} />
            <Route path="/finance/settlements" element={<ProtectedRoute><SettlementDashboardPage /></ProtectedRoute>} />
            <Route path="/finance/reconciliation" element={<ProtectedRoute><ReconciliationDashboardPage /></ProtectedRoute>} />
            <Route path="/finance/accounting" element={<ProtectedRoute><AccountingDashboardPage /></ProtectedRoute>} />
            <Route path="/finance/gst" element={<ProtectedRoute><GSTDashboardPage /></ProtectedRoute>} />
            <Route path="/finance/tally" element={<ProtectedRoute><TallySyncDashboardPage /></ProtectedRoute>} />
            <Route path="/finance/analytics" element={<ProtectedRoute><FinanceAnalyticsPage /></ProtectedRoute>} />
            <Route path="/procurement" element={<ProtectedRoute><ProcurementHubPage /></ProtectedRoute>} />
            <Route path="/procurement/planning" element={<ProtectedRoute><MRPPlanningPage /></ProtectedRoute>} />
            <Route path="/procurement/requisitions" element={<ProtectedRoute><RequisitionPage /></ProtectedRoute>} />
            <Route path="/procurement/rfq" element={<ProtectedRoute><RFQManagerPage /></ProtectedRoute>} />
            <Route path="/procurement/purchase-orders" element={<ProtectedRoute><PurchaseOrderPage /></ProtectedRoute>} />
            <Route path="/procurement/grn" element={<ProtectedRoute><GoodsReceiptPage /></ProtectedRoute>} />
            <Route path="/procurement/analytics" element={<ProtectedRoute><ProcurementAnalyticsPage /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><SupplierHubPage /></ProtectedRoute>} />
            <Route path="/suppliers/directory" element={<ProtectedRoute><SupplierDirectoryPage /></ProtectedRoute>} />
            <Route path="/suppliers/onboarding" element={<ProtectedRoute><SupplierOnboardingPage /></ProtectedRoute>} />
            <Route path="/suppliers/contracts" element={<ProtectedRoute><ContractManagerPage /></ProtectedRoute>} />
            <Route path="/suppliers/compliance" element={<ProtectedRoute><SupplierComplianceDashboardPage /></ProtectedRoute>} />
            <Route path="/suppliers/performance" element={<ProtectedRoute><PerformanceDashboardPage /></ProtectedRoute>} />
            <Route path="/suppliers/risk" element={<ProtectedRoute><RiskDashboardPage /></ProtectedRoute>} />
            <Route path="/suppliers/analytics" element={<ProtectedRoute><SupplierAnalyticsPage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><CustomerHubPage /></ProtectedRoute>} />
            <Route path="/customers/directory" element={<ProtectedRoute><CustomerDirectoryPage /></ProtectedRoute>} />
            <Route path="/customers/360" element={<ProtectedRoute><Customer360Page /></ProtectedRoute>} />
            <Route path="/customers/segments" element={<ProtectedRoute><SegmentationDashboardPage /></ProtectedRoute>} />
            <Route path="/customers/loyalty" element={<ProtectedRoute><LoyaltyDashboardPage /></ProtectedRoute>} />
            <Route path="/customers/wallet" element={<ProtectedRoute><WalletDashboardPage /></ProtectedRoute>} />
            <Route path="/customers/support" element={<ProtectedRoute><SupportCenterPage /></ProtectedRoute>} />
            <Route path="/customers/analytics" element={<ProtectedRoute><CustomerAnalyticsPage /></ProtectedRoute>} />
            <Route path="/runtime" element={<ProtectedRoute><RuntimeHubPage /></ProtectedRoute>} />
            <Route path="/runtime/modules" element={<ProtectedRoute><ModuleManagerPage /></ProtectedRoute>} />
            <Route path="/modules" element={<ProtectedRoute><EnterpriseModuleDashboardPage /></ProtectedRoute>} />
            <Route path="/architecture/consolidation" element={<ProtectedRoute><ArchitectureConsolidationPage /></ProtectedRoute>} />
            <Route path="/architecture/canonical" element={<ProtectedRoute><CanonicalDomainRegistryPage /></ProtectedRoute>} />
            <Route path="/architecture/compliance" element={<ProtectedRoute><EnterpriseComplianceCenterPage /></ProtectedRoute>} />
            <Route path="/runtime/installed" element={<ProtectedRoute><InstalledModulesPage /></ProtectedRoute>} />
            <Route path="/runtime/dependencies" element={<ProtectedRoute><RuntimeDependencyGraphPage /></ProtectedRoute>} />
            <Route path="/runtime/health" element={<ProtectedRoute><RuntimeHealthPage /></ProtectedRoute>} />
            <Route path="/runtime/activity" element={<ProtectedRoute><RuntimeActivityPage /></ProtectedRoute>} />
            <Route path="/runtime/upgrades" element={<ProtectedRoute><UpgradeManagerPage /></ProtectedRoute>} />
            <Route path="/members" element={<ProtectedRoute><MemberDirectoryPage /></ProtectedRoute>} />
            <Route path="/members/search" element={<ProtectedRoute><MemberSearchPage /></ProtectedRoute>} />
            <Route path="/members/manage" element={<ProtectedRoute><MembershipManagementPage /></ProtectedRoute>} />
            <Route path="/members/:id" element={<ProtectedRoute><MemberDetailPage /></ProtectedRoute>} />
            <Route path="/invitations/bulk" element={<ProtectedRoute><BulkInvitationPage /></ProtectedRoute>} />
            <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
            <Route path="/teams/:id/dashboard" element={<ProtectedRoute><TeamDashboardPage /></ProtectedRoute>} />
            <Route path="/teams/:id" element={<ProtectedRoute><TeamDetailPage /></ProtectedRoute>} />
            <Route element={<ProtectedLayout />}>
            <Route path="/onboarding" element={<OnboardingWizardPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/alpha" element={<AlphaReadinessPage />} />
            <Route path="/amazon/connect" element={<ConnectDashboardPage />} />
            <Route path="/amazon/connect/settings" element={<AmazonApplicationSettingsPage />} />
            <Route path="/amazon/connect/callback" element={<AmazonOAuthCallbackPage />} />
            <Route path="/amazon/system-explorer" element={<ProtectedRoute><SystemExplorerPage /></ProtectedRoute>} />
            <Route path="/platform/system-explorer" element={<ProtectedRoute><SystemExplorerPage /></ProtectedRoute>} />
            <Route path="/amazon/catalog" element={<ProtectedRoute><MarketSyncProductsPage /></ProtectedRoute>} />
            <Route path="/amazon/catalog-pim" element={<CatalogDashboardPage />} />
            <Route path="/amazon/inventory" element={<ProtectedRoute><MarketSyncInventoryPage /></ProtectedRoute>} />
            <Route path="/amazon/orders" element={<ProtectedRoute><MarketSyncOrdersPage /></ProtectedRoute>} />
            <Route path="/amazon/order-items" element={<ProtectedRoute><MarketSyncOrderItemsPage /></ProtectedRoute>} />
            <Route path="/amazon/spapi-v2/orders" element={<ProtectedRoute><SpapiV2OrdersPage /></ProtectedRoute>} />
            <Route path="/amazon/spapi-v2/order-items" element={<ProtectedRoute><SpapiV2OrderItemsPage /></ProtectedRoute>} />
            <Route path="/amazon/finance" element={<ProtectedRoute><MarketSyncFinancePage /></ProtectedRoute>} />
            <Route path="/amazon/pricing" element={<ProtectedRoute><MarketSyncPricingPage /></ProtectedRoute>} />
            <Route path="/amazon/shipments" element={<ProtectedRoute><CommercePlaceholderPage kind="shipments" /></ProtectedRoute>} />
            <Route path="/amazon/notifications" element={<ProtectedRoute><CommercePlaceholderPage kind="notifications" /></ProtectedRoute>} />
            <Route path="/amazon/discovery" element={<ProtectedRoute><AmazonDiscoveryPage /></ProtectedRoute>} />
            <Route path="/amazon/inventory-legacy" element={<InventoryDashboardPage />} />
            <Route path="/amazon/orders-legacy" element={<OrdersDashboardPage />} />
            <Route path="/amazon/growth" element={<AmazonGrowthPage />} />
            <Route path="/amazon/advertising" element={<AmazonAdvertisingIntelPage />} />
            <Route path="/amazon/inventory-intel" element={<InventoryIntelPage />} />
            <Route path="/amazon/pricing-intel" element={<PricingIntelPage />} />
            <Route path="/amazon/market-intel" element={<MarketIntelPage />} />
            <Route path="/amazon/voc-intel" element={<VoCIntelPage />} />
            <Route path="/amazon/content-intel" element={<ContentIntelPage />} />
            <Route path="/planning/procurement" element={<ProcurementPlanningPage />} />
            <Route path="/planning/supply" element={<SupplyPlanningPage />} />
            <Route path="/planning/warehouse-capacity" element={<WarehouseCapacityPlanningPage />} />
            <Route path="/planning/scenarios" element={<ScenarioPlanningPage />} />
            <Route path="/planning/financial" element={<FinancialPlanningPage />} />
            <Route path="/planning/sop" element={<SOPPlanningPage />} />
            <Route path="/planning/analytics" element={<PlanningAnalyticsPage />} />
            <Route path="/planning/executive" element={<PlanningExecutiveCockpitPage />} />
            <Route path="/planning/decisions" element={<PlanningDecisionIntelPage />} />
            <Route path="/planning/autonomous" element={<AutonomousPlanningPage />} />
            <Route path="/planning/inventory" element={<InventoryPlanningPage />} />
            <Route path="/planning/ai-forecast" element={<AIForecastPage />} />
            <Route path="/planning/forecasts" element={<ForecastEnginePage />} />
            <Route path="/planning/demand" element={<DemandPlanningPage />} />
            <Route path="/planning" element={<PlanningPlatformPage />} />
            <Route path="/strategy/growth-planner" element={<GrowthPlannerPage />} />
            <Route path="/strategy/brand-portfolio" element={<BrandPortfolioPage />} />
            <Route path="/amazon/search-intel" element={<SearchIntelPage />} />
            <Route path="/amazon/experiment-intel" element={<ExperimentIntelPage />} />
            <Route path="/executive/daily-ops" element={<DailyOpsPage />} />
            <Route path="/operations/hub" element={<OpsHubPage />} />
            <Route path="/finance/intelligence" element={<FinIntelPage />} />
            <Route path="/amazon/catalog-ops" element={<CatalogOpsPage />} />
            <Route path="/amazon/order-ops" element={<OrderOpsPage />} />
            <Route path="/amazon/fba-ops" element={<FBAOpsPage />} />
            <Route path="/amazon/post-sales-ops" element={<PostSalesOpsPage />} />
            <Route path="/amazon/brand-ops" element={<BrandOpsPage />} />
            <Route path="/amazon/customer-growth" element={<CustomerGrowthPage />} />
            <Route path="/amazon/promotion-ops" element={<PromotionOpsPage />} />
            <Route path="/amazon/bi-ops" element={<BIOpsPage />} />
            <Route path="/amazon/finance-ops" element={<FinanceOpsPage />} />
            <Route path="/amazon/revenue-ops" element={<RevenueOpsPage />} />
            <Route path="/platform/experience" element={<ExperienceArchitecturePage />} />
            <Route path="/platform/design-system" element={<DesignSystemPage />} />
            <Route path="/platform/aix" element={<AIXExperiencePage />} />
            <Route path="/platform/workspace" element={<WorkspaceArchitecturePage />} />
            <Route path="/platform/human-experience" element={<HumanExperiencePage />} />
            <Route path="/platform/role-experience" element={<RoleExperiencePage />} />
            <Route path="/platform/adaptive-experience" element={<AdaptiveExperiencePage />} />
            <Route path="/platform/motion-experience" element={<MotionExperiencePage />} />
            <Route path="/platform/identity-platform" element={<IdentityPlatformPage />} />
            <Route path="/platform/executive-home" element={<ExecutiveHomePage />} />
            <Route path="/platform/marketplace-connection" element={<MarketplaceConnectionPage />} />
            <Route path="/platform/product-intelligence" element={<ProductIntelligencePage />} />
            <Route path="/platform/content-studio" element={<ContentStudioPage />} />
            <Route path="/platform/creative-studio" element={<CreativeStudioPlatformPage />} />
            <Route path="/platform/catalog-operations" element={<CatalogOperationsPage />} />
            <Route path="/platform/revenue-intelligence" element={<RevenueIntelligencePage />} />
            <Route path="/platform/supply-chain" element={<SupplyChainPage />} />
            <Route path="/platform/customer-operations" element={<CustomerOperationsPage />} />
            <Route path="/platform/financial-intelligence" element={<FinancialIntelligencePage />} />
            <Route path="/platform/platform-administration" element={<PlatformAdministrationPage />} />
            <Route path="/platform/executive-intelligence" element={<ExecutiveIntelligencePage />} />
            <Route path="/platform/automation-studio" element={<AutomationStudioPage />} />
            <Route path="/platform/knowledge-center" element={<KnowledgeCenterPage />} />
            <Route path="/platform/mobile-companion" element={<MobileCompanionPage />} />
            <Route path="/platform/unified-commerce" element={<UnifiedCommercePage />} />
            <Route path="/platform/customer-success" element={<CustomerSuccessPage />} />
            <Route path="/platform/enterprise-integration" element={<EnterpriseIntegrationPage />} />
            <Route path="/platform/integration-governance" element={<IntegrationGovernancePage />} />
            <Route path="/platform/enterprise-quality" element={<EnterpriseQualityPage />} />
            <Route path="/platform/enterprise-devops" element={<EnterpriseDevOpsPage />} />
            <Route path="/platform/enterprise-acceptance" element={<EnterpriseAcceptancePage />} />
            <Route path="/platform/enterprise-beta" element={<EnterpriseBetaPage />} />
            <Route path="/platform/enterprise-ga" element={<EnterpriseGAPage />} />
            <Route path="/platform/master-constitution" element={<MasterPlatformConstitutionPage />} />
            <Route path="/platform/architecture-governance" element={<ArchitectureGovernancePage />} />
            <Route path="/platform/engineering-governance" element={<EngineeringGovernancePage />} />
            <Route path="/platform/ai-governance" element={<EnterpriseAIGovernancePage />} />
            <Route path="/platform/security-governance" element={<SecurityGovernancePage />} />
            <Route path="/platform/product-governance" element={<ProductGovernancePage />} />
            <Route path="/platform/data-governance" element={<DataGovernancePage />} />
            <Route path="/platform/release-governance" element={<ReleaseGovernancePage />} />
            <Route path="/platform/master-development-plan" element={<MasterDevelopmentPlanPage />} />
            <Route path="/platform/master-dependency-matrix" element={<MasterDependencyMatrixPage />} />
            <Route path="/platform/coding-constitution" element={<CodingConstitutionPage />} />
            <Route path="/platform/repository-blueprint" element={<RepositoryBlueprintPage />} />
            <Route path="/platform/dev-environment-bootstrap" element={<DevEnvironmentBootstrapPage />} />
            <Route path="/ai/listing" element={<ListingOptimizerDashboardPage />} />
            <Route path="/ai/creative" element={<CreativeStudioDashboardPage />} />
            <Route path="/publishing" element={<PublishingDashboardPage />} />
            <Route path="/analytics" element={<BIHubPage />} />
            <Route path="/analytics/bi" element={<BIHubPage />} />
            <Route path="/analytics/executive" element={<ProtectedRoute><ExecutiveDashboardPage /></ProtectedRoute>} />
            <Route path="/analytics/kpis" element={<ProtectedRoute><KPIManagerPage /></ProtectedRoute>} />
            <Route path="/analytics/forecast" element={<ProtectedRoute><ForecastCenterPage /></ProtectedRoute>} />
            <Route path="/analytics/decision" element={<ProtectedRoute><DecisionCenterPage /></ProtectedRoute>} />
            <Route path="/analytics/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
            <Route path="/analytics/search" element={<ProtectedRoute><SemanticSearchPage /></ProtectedRoute>} />
            <Route path="/analytics/intelligence" element={<ProtectedRoute><DecisionIntelligenceDashboardPage /></ProtectedRoute>} />
            <Route path="/workflows" element={<OrchestrationDashboardPage />} />
            <Route path="/workflows/center" element={<ProtectedRoute><WorkflowCenterPage /></ProtectedRoute>} />
            <Route path="/workflows/sagas" element={<ProtectedRoute><SagaDashboardPage /></ProtectedRoute>} />
            <Route path="/copilot" element={<OrchestrationDashboardPage />} />
            <Route path="/integrations" element={<IntegrationHubDashboardPage />} />
            <Route path="/integrations/marketplace" element={<ProtectedRoute><ConnectorMarketplacePage /></ProtectedRoute>} />
            <Route path="/integrations/gateway" element={<ProtectedRoute><APIGatewayPage /></ProtectedRoute>} />
            <Route path="/integrations/webhooks" element={<ProtectedRoute><WebhookManagerPage /></ProtectedRoute>} />
            <Route path="/integrations/developer" element={<ProtectedRoute><IntegrationsDeveloperPortalPage /></ProtectedRoute>} />
            <Route path="/integrations/transformations" element={<ProtectedRoute><TransformationDesignerPage /></ProtectedRoute>} />
            <Route path="/integrations/health" element={<ProtectedRoute><ConnectorHealthPage /></ProtectedRoute>} />
            <Route path="/integrations/analytics" element={<ProtectedRoute><APIAnalyticsPage /></ProtectedRoute>} />
            <Route path="/integrations/logs" element={<ProtectedRoute><ConnectorLogsPage /></ProtectedRoute>} />
            <Route path="/billing" element={<BillingDashboardPage />} />
            <Route path="/commercial" element={<ProtectedRoute><CommercialHubPage /></ProtectedRoute>} />
            <Route path="/commercial/licenses" element={<ProtectedRoute><LicenseManagerPage /></ProtectedRoute>} />
            <Route path="/commercial/bundles" element={<ProtectedRoute><BundleManagerPage /></ProtectedRoute>} />
            <Route path="/commercial/usage" element={<ProtectedRoute><UsageDashboardPage /></ProtectedRoute>} />
            <Route path="/commercial/credits" element={<ProtectedRoute><CreditWalletPage /></ProtectedRoute>} />
            <Route path="/configuration" element={<ProtectedRoute><ConfigurationHubPage /></ProtectedRoute>} />
            <Route path="/configuration/settings" element={<ProtectedRoute><SettingsManagerPage /></ProtectedRoute>} />
            <Route path="/configuration/feature-flags" element={<ProtectedRoute><FeatureFlagManagerPage /></ProtectedRoute>} />
            <Route path="/configuration/secrets" element={<ProtectedRoute><SecretVaultPage /></ProtectedRoute>} />
            <Route path="/configuration/localization" element={<ProtectedRoute><LocalizationManagerPage /></ProtectedRoute>} />
            <Route path="/configuration/preferences" element={<ProtectedRoute><PreferencesCenterPage /></ProtectedRoute>} />
            <Route path="/configuration/history" element={<ProtectedRoute><ConfigurationHistoryPage /></ProtectedRoute>} />
            <Route path="/configuration/import-export" element={<ProtectedRoute><ImportExportWizardPage /></ProtectedRoute>} />
            <Route path="/settings/master-data" element={<ProtectedRoute><MasterDataHubPage /></ProtectedRoute>} />
            <Route path="/settings/master-data/warehouses" element={<ProtectedRoute><WarehouseMasterPage /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><EventHubPage /></ProtectedRoute>} />
            <Route path="/events/stream" element={<ProtectedRoute><LiveEventStreamPage /></ProtectedRoute>} />
            <Route path="/events/explorer" element={<ProtectedRoute><EventExplorerPage /></ProtectedRoute>} />
            <Route path="/events/registry" element={<ProtectedRoute><SchemaRegistryPage /></ProtectedRoute>} />
            <Route path="/events/queues" element={<ProtectedRoute><QueueMonitorPage /></ProtectedRoute>} />
            <Route path="/events/dead-letter" element={<ProtectedRoute><DeadLetterViewerPage /></ProtectedRoute>} />
            <Route path="/events/replay" element={<ProtectedRoute><ReplayManagerPage /></ProtectedRoute>} />
            <Route path="/events/messaging" element={<ProtectedRoute><MessagingReliabilityPage /></ProtectedRoute>} />
            <Route path="/events/commerce" element={<ProtectedRoute><CommerceEventCatalogPage /></ProtectedRoute>} />
            <Route path="/platform/event-mesh" element={<ProtectedRoute><EventMeshGovernancePage /></ProtectedRoute>} />
            <Route path="/plugins" element={<ProtectedRoute><PluginHubPage /></ProtectedRoute>} />
            <Route path="/plugins/manager" element={<ProtectedRoute><PluginManagerPage /></ProtectedRoute>} />
            <Route path="/plugins/marketplace" element={<ProtectedRoute><PluginMarketplacePage /></ProtectedRoute>} />
            <Route path="/plugins/publishers" element={<ProtectedRoute><PublisherDashboardPage /></ProtectedRoute>} />
            <Route path="/plugins/health" element={<ProtectedRoute><PluginHealthPage /></ProtectedRoute>} />
            <Route path="/automation" element={<ProtectedRoute><AutomationHubPage /></ProtectedRoute>} />
            <Route path="/automation/designer" element={<ProtectedRoute><WorkflowDesignerPage /></ProtectedRoute>} />
            <Route path="/automation/rules" element={<ProtectedRoute><RuleBuilderPage /></ProtectedRoute>} />
            <Route path="/automation/executions" element={<ProtectedRoute><ExecutionMonitorPage /></ProtectedRoute>} />
            <Route path="/automation/approvals" element={<ProtectedRoute><ApprovalCenterPage /></ProtectedRoute>} />
            <Route path="/automation/agents" element={<ProtectedRoute><AgentConsolePage /></ProtectedRoute>} />
            <Route path="/automation/templates" element={<ProtectedRoute><TemplateLibraryPage /></ProtectedRoute>} />
            <Route path="/communication" element={<ProtectedRoute><CommunicationHubPage /></ProtectedRoute>} />
            <Route path="/communication/campaigns" element={<ProtectedRoute><CampaignManagerPage /></ProtectedRoute>} />
            <Route path="/communication/templates" element={<ProtectedRoute><TemplateBuilderPage /></ProtectedRoute>} />
            <Route path="/communication/notifications" element={<ProtectedRoute><NotificationCenterPage /></ProtectedRoute>} />
            <Route path="/communication/providers" element={<ProtectedRoute><ProviderManagerPage /></ProtectedRoute>} />
            <Route path="/communication/delivery" element={<ProtectedRoute><DeliveryMonitorPage /></ProtectedRoute>} />
            <Route path="/communication/analytics" element={<ProtectedRoute><CommunicationAnalyticsPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><DocumentCenterPage /></ProtectedRoute>} />
            <Route path="/documents/repository" element={<ProtectedRoute><RepositoryBrowserPage /></ProtectedRoute>} />
            <Route path="/documents/folders" element={<ProtectedRoute><FolderManagerPage /></ProtectedRoute>} />
            <Route path="/documents/ocr" element={<ProtectedRoute><OCRReviewPage /></ProtectedRoute>} />
            <Route path="/documents/extraction" element={<ProtectedRoute><AIExtractionViewerPage /></ProtectedRoute>} />
            <Route path="/documents/signatures" element={<ProtectedRoute><SignatureCenterPage /></ProtectedRoute>} />
            <Route path="/documents/search" element={<ProtectedRoute><SearchConsolePage /></ProtectedRoute>} />
            <Route path="/documents/compliance" element={<ProtectedRoute><DocumentComplianceDashboardPage /></ProtectedRoute>} />
            <Route path="/developer" element={<DeveloperPortalPage />} />
            <Route path="/platform/api-gateway" element={<ProtectedRoute><APIGatewayGovernancePage /></ProtectedRoute>} />
            <Route path="/platform/persistence-governance" element={<ProtectedRoute><PersistenceGovernancePage /></ProtectedRoute>} />
            <Route path="/platform/semantic-governance" element={<ProtectedRoute><SemanticGovernancePage /></ProtectedRoute>} />
            <Route path="/platform/ai-operations" element={<ProtectedRoute><AIPlatformOperationsPage /></ProtectedRoute>} />
            <Route path="/platform/commercial-governance" element={<ProtectedRoute><CommercialGovernancePage /></ProtectedRoute>} />
            <Route path="/platform/observability-governance" element={<ProtectedRoute><ObservabilityGovernancePage /></ProtectedRoute>} />
            <Route path="/platform/foundation-certification" element={<ProtectedRoute><FoundationCertificationPage /></ProtectedRoute>} />
            <Route path="/platform/product-family-constitution" element={<ProtectedRoute><ProductFamilyConstitutionPage /></ProtectedRoute>} />
            <Route path="/platform/engineering-execution-bible" element={<ProtectedRoute><EngineeringExecutionBiblePage /></ProtectedRoute>} />
            <Route path="/platform/engineering-backlog-sprint-planning" element={<ProtectedRoute><EngineeringBacklogSprintPlanningPage /></ProtectedRoute>} />
            <Route path="/platform/architecture-decision-records" element={<ProtectedRoute><ArchitectureDecisionRecordsPage /></ProtectedRoute>} />
            <Route path="/platform/repository-architecture-bible" element={<ProtectedRoute><RepositoryArchitectureBiblePage /></ProtectedRoute>} />
            <Route path="/platform/repository-bootstrap-production-foundation" element={<ProtectedRoute><RepositoryBootstrapProductionFoundationPage /></ProtectedRoute>} />
            <Route path="/platform/enterprise-identity-platform-dev101" element={<ProtectedRoute><EnterpriseIdentityPlatformPage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-integration-platform-dev102" element={<ProtectedRoute><MarketplaceIntegrationPlatformPage /></ProtectedRoute>} />
            <Route path="/platform/daily-growth-command-center-dev103" element={<ProtectedRoute><DailyGrowthCommandCenterDev103Page /></ProtectedRoute>} />
            <Route path="/platform/product-workspace-asin360-dev104" element={<ProtectedRoute><ProductWorkspaceDev104Page /></ProtectedRoute>} />
            <Route path="/platform/marketplace-dna-engine-dev105" element={<ProtectedRoute><MarketplaceDNAEngineDev105Page /></ProtectedRoute>} />
            <Route path="/platform/listing-growth-intelligence-dev106" element={<ProtectedRoute><ListingGrowthIntelligenceDev106Page /></ProtectedRoute>} />
            <Route path="/platform/advertising-growth-intelligence-dev107" element={<ProtectedRoute><AdvertisingGrowthIntelligenceDev107Page /></ProtectedRoute>} />
            <Route path="/platform/fba-growth-intelligence-dev108" element={<ProtectedRoute><FBAGrowthIntelligenceDev108Page /></ProtectedRoute>} />
            <Route path="/platform/profit-intelligence-dev109" element={<ProtectedRoute><ProfitIntelligenceDev109Page /></ProtectedRoute>} />
            <Route path="/platform/marketplace-operating-brain-dev110" element={<ProtectedRoute><MarketplaceOperatingBrainDev110Page /></ProtectedRoute>} />
            <Route path="/platform/production-readiness-prt001" element={<ProtectedRoute><ProductionReadinessPRT001Page /></ProtectedRoute>} />
            <Route path="/platform/recommendation-validation-val001" element={<ProtectedRoute><RecommendationValidationVal001Page /></ProtectedRoute>} />
            <Route path="/platform/intelligence-qa-iqa001" element={<ProtectedRoute><IntelligenceQAIqa001Page /></ProtectedRoute>} />
            <Route path="/platform/autonomous-ops-aow001" element={<ProtectedRoute><AutonomousOperationsAow001Page /></ProtectedRoute>} />
            <Route path="/platform/ai-governance-gov001" element={<ProtectedRoute><AIGovernanceGov001Page /></ProtectedRoute>} />
            <Route path="/platform/engineering-excellence-exl001" element={<ProtectedRoute><EngineeringExcellenceExl001Page /></ProtectedRoute>} />
            <Route path="/platform/customer-value-cvd001" element={<ProtectedRoute><CustomerValueCvd001Page /></ProtectedRoute>} />
            <Route path="/platform/experience-design-xdb001" element={<ProtectedRoute><ExperienceDesignXdb001Page /></ProtectedRoute>} />
            <Route path="/platform/screen-review-xdb002" element={<ProtectedRoute><ScreenReviewXdb002Page /></ProtectedRoute>} />
            <Route path="/platform/internal-alpha-alpha001" element={<ProtectedRoute><InternalAlphaAlpha001Page /></ProtectedRoute>} />
            <Route path="/platform/enterprise-design-system" element={<ProtectedRoute><EnterpriseDesignSystemPage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-intelligence-bible" element={<ProtectedRoute><MarketplaceIntelligenceBiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-kpi-bible" element={<ProtectedRoute><MarketplaceKPIBiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-dna-bible" element={<ProtectedRoute><MarketplaceDNABiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-growth-experiment-bible" element={<ProtectedRoute><MarketplaceGrowthExperimentBiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-mos-bible" element={<ProtectedRoute><MarketplaceMOSBiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-msi-bible" element={<ProtectedRoute><MarketplaceMSIBiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-mvp-bible" element={<ProtectedRoute><MarketplaceMVPBiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-screen-bible" element={<ProtectedRoute><MarketplaceScreenBiblePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-capability-architecture" element={<ProtectedRoute><MarketplaceCapabilityArchitecturePage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-intelligence-v1-prd" element={<ProtectedRoute><MarketplaceIntelligenceV1PRDPage /></ProtectedRoute>} />
            <Route path="/platform/daily-growth-command-center-prd" element={<ProtectedRoute><DailyGrowthCommandCenterPRDPage /></ProtectedRoute>} />
            <Route path="/platform/product-workspace-asin360-prd" element={<ProtectedRoute><ProductWorkspaceASIN360PRDPage /></ProtectedRoute>} />
            <Route path="/platform/listing-growth-engine-prd" element={<ProtectedRoute><ListingGrowthEnginePRDPage /></ProtectedRoute>} />
            <Route path="/platform/advertising-growth-engine-prd" element={<ProtectedRoute><AdvertisingGrowthEnginePRDPage /></ProtectedRoute>} />
            <Route path="/platform/fba-growth-engine-prd" element={<ProtectedRoute><FBAGrowthEnginePRDPage /></ProtectedRoute>} />
            <Route path="/platform/profit-intelligence-leakage-engine-prd" element={<ProtectedRoute><ProfitIntelligenceLeakageEnginePRDPage /></ProtectedRoute>} />
            <Route path="/platform/growth-experiment-engine-prd" element={<ProtectedRoute><GrowthExperimentEnginePRDPage /></ProtectedRoute>} />
            <Route path="/platform/marketplace-operating-brain-prd" element={<ProtectedRoute><MarketplaceOperatingBrainPRDPage /></ProtectedRoute>} />
            <Route path="/knowledge" element={<ProtectedRoute><KnowledgeHubPage /></ProtectedRoute>} />
            <Route path="/knowledge/studio" element={<ProtectedRoute><KnowledgePlatformPage /></ProtectedRoute>} />
            <Route path="/knowledge/editor" element={<ProtectedRoute><KnowledgeEditorPage /></ProtectedRoute>} />
            <Route path="/knowledge/graph" element={<ProtectedRoute><KnowledgeGraphViewerPage /></ProtectedRoute>} />
            <Route path="/knowledge/search" element={<ProtectedRoute><KnowledgeSemanticSearchPage /></ProtectedRoute>} />
            <Route path="/knowledge/memory" element={<ProtectedRoute><MemoryViewerPage /></ProtectedRoute>} />
            <Route path="/knowledge/citations" element={<ProtectedRoute><CitationExplorerPage /></ProtectedRoute>} />
            <Route path="/knowledge/analytics" element={<ProtectedRoute><KnowledgeAnalyticsPage /></ProtectedRoute>} />
            <Route path="/observability" element={<ProtectedRoute><OperationsCenterPage /></ProtectedRoute>} />
            <Route path="/observability/health" element={<ProtectedRoute><ObservabilityHealthDashboardPage /></ProtectedRoute>} />
            <Route path="/observability/metrics" element={<ProtectedRoute><MetricsExplorerPage /></ProtectedRoute>} />
            <Route path="/observability/logs" element={<ProtectedRoute><LogExplorerPage /></ProtectedRoute>} />
            <Route path="/observability/traces" element={<ProtectedRoute><TraceViewerPage /></ProtectedRoute>} />
            <Route path="/observability/incidents" element={<ProtectedRoute><IncidentCenterPage /></ProtectedRoute>} />
            <Route path="/observability/alerts" element={<ProtectedRoute><AlertManagerPage /></ProtectedRoute>} />
            <Route path="/observability/runbooks" element={<ProtectedRoute><RunbookCenterPage /></ProtectedRoute>} />
            <Route path="/observability/capacity" element={<ProtectedRoute><CapacityDashboardPage /></ProtectedRoute>} />
            <Route path="/observability/performance" element={<ProtectedRoute><ObservabilityPerformanceDashboardPage /></ProtectedRoute>} />
            <Route path="/saas" element={<ProtectedRoute><SaaSHubPage /></ProtectedRoute>} />
            <Route path="/saas/tenants" element={<ProtectedRoute><TenantManagerPage /></ProtectedRoute>} />
            <Route path="/saas/organizations" element={<ProtectedRoute><OrganizationManagerPage /></ProtectedRoute>} />
            <Route path="/saas/branding" element={<ProtectedRoute><BrandingCenterPage /></ProtectedRoute>} />
            <Route path="/saas/provisioning" element={<ProtectedRoute><ProvisioningCenterPage /></ProtectedRoute>} />
            <Route path="/saas/support" element={<ProtectedRoute><SupportConsolePage /></ProtectedRoute>} />
            <Route path="/saas/health" element={<ProtectedRoute><TenantHealthPage /></ProtectedRoute>} />
            <Route path="/saas/analytics" element={<ProtectedRoute><TenantAnalyticsPage /></ProtectedRoute>} />
            <Route path="/saas/whitelabel" element={<ProtectedRoute><WhiteLabelManagerPage /></ProtectedRoute>} />
            <Route path="/saas/marketplaces" element={<ProtectedRoute><MarketplaceManagerPage /></ProtectedRoute>} />
            <Route path="/decision" element={<ProtectedRoute><DecisionHubPage /></ProtectedRoute>} />
            <Route path="/decision/recommendations" element={<ProtectedRoute><RecommendationCenterPage /></ProtectedRoute>} />
            <Route path="/decision/simulations" element={<ProtectedRoute><SimulationStudioPage /></ProtectedRoute>} />
            <Route path="/decision/optimizations" element={<ProtectedRoute><OptimizationCenterPage /></ProtectedRoute>} />
            <Route path="/decision/agents" element={<ProtectedRoute><AIAgentConsolePage /></ProtectedRoute>} />
            <Route path="/decision/timeline" element={<ProtectedRoute><DecisionTimelinePage /></ProtectedRoute>} />
            <Route path="/decision/risk" element={<ProtectedRoute><DecisionRiskDashboardPage /></ProtectedRoute>} />
            <Route path="/decision/approvals" element={<ProtectedRoute><ApprovalDashboardPage /></ProtectedRoute>} />
            <Route path="/decision/cockpit" element={<ProtectedRoute><ExecutiveCockpitPage /></ProtectedRoute>} />
            <Route path="/resilience" element={<ProtectedRoute><ResilienceHubPage /></ProtectedRoute>} />
            <Route path="/resilience/backups" element={<ProtectedRoute><BackupCenterPage /></ProtectedRoute>} />
            <Route path="/resilience/recovery" element={<ProtectedRoute><RecoveryCenterPage /></ProtectedRoute>} />
            <Route path="/resilience/replication" element={<ProtectedRoute><ReplicationMonitorPage /></ProtectedRoute>} />
            <Route path="/resilience/disaster" element={<ProtectedRoute><DisasterRecoveryConsolePage /></ProtectedRoute>} />
            <Route path="/resilience/runbooks" element={<ProtectedRoute><RecoveryRunbooksPage /></ProtectedRoute>} />
            <Route path="/resilience/integrity" element={<ProtectedRoute><IntegrityDashboardPage /></ProtectedRoute>} />
            <Route path="/resilience/continuity" element={<ProtectedRoute><BusinessContinuityDashboardPage /></ProtectedRoute>} />
            <Route path="/resilience/restore" element={<ProtectedRoute><RestoreWizardPage /></ProtectedRoute>} />
            <Route path="/security" element={<ProtectedRoute><SecurityHubPage /></ProtectedRoute>} />
            <Route path="/security/threats" element={<ProtectedRoute><ThreatCenterPage /></ProtectedRoute>} />
            <Route path="/security/incidents" element={<ProtectedRoute><SecurityIncidentCenterPage /></ProtectedRoute>} />
            <Route path="/security/identity" element={<ProtectedRoute><IdentityGovernancePage /></ProtectedRoute>} />
            <Route path="/security/risk" element={<ProtectedRoute><RiskCenterPage /></ProtectedRoute>} />
            <Route path="/security/compliance" element={<ProtectedRoute><ComplianceCenterPage /></ProtectedRoute>} />
            <Route path="/security/audit" element={<ProtectedRoute><AuditExplorerPage /></ProtectedRoute>} />
            <Route path="/security/secrets" element={<ProtectedRoute><SecretsVaultPage /></ProtectedRoute>} />
            <Route path="/security/policies" element={<ProtectedRoute><PolicyManagerPage /></ProtectedRoute>} />
            <Route path="/security/investigation" element={<ProtectedRoute><InvestigationConsolePage /></ProtectedRoute>} />
            <Route path="/optimization" element={<ProtectedRoute><OptimizationHubPage /></ProtectedRoute>} />
            <Route path="/optimization/recommendations" element={<ProtectedRoute><OptimizationRecommendationCenterPage /></ProtectedRoute>} />
            <Route path="/optimization/cost" element={<ProtectedRoute><CostIntelligencePage /></ProtectedRoute>} />
            <Route path="/optimization/capacity" element={<ProtectedRoute><CapacityPlanningPage /></ProtectedRoute>} />
            <Route path="/optimization/performance" element={<ProtectedRoute><PerformanceEngineeringPage /></ProtectedRoute>} />
            <Route path="/optimization/self-healing" element={<ProtectedRoute><SelfHealingCenterPage /></ProtectedRoute>} />
            <Route path="/optimization/benchmarks" element={<ProtectedRoute><BenchmarkDashboardPage /></ProtectedRoute>} />
            <Route path="/optimization/health" element={<ProtectedRoute><PlatformHealthOverviewPage /></ProtectedRoute>} />
            <Route path="/optimization/timeline" element={<ProtectedRoute><ExecutionTimelinePage /></ProtectedRoute>} />
            <Route path="/governance" element={<GovernancePlatformPage />} />
            <Route path="/sre" element={<SREPlatformPage />} />
            <Route path="/command" element={<CommandCenterPage />} />
            {Object.entries(MODULE_PAGES).filter(([path]) => path !== '/amazon/connect' && path !== '/amazon/catalog' && path !== '/amazon/catalog-ops' && path !== '/amazon/order-ops' && path !== '/amazon/fba-ops' && path !== '/amazon/post-sales-ops' && path !== '/amazon/brand-ops' && path !== '/amazon/customer-growth' && path !== '/amazon/promotion-ops' && path !== '/amazon/bi-ops' && path !== '/amazon/finance-ops' && path !== '/amazon/revenue-ops' && path !== '/amazon/discovery' && path !== '/platform/experience' && path !== '/platform/design-system' && path !== '/platform/aix' && path !== '/platform/workspace' && path !== '/platform/human-experience' && path !== '/platform/role-experience' && path !== '/platform/adaptive-experience' && path !== '/platform/motion-experience' && path !== '/platform/identity-platform' && path !== '/platform/executive-home' && path !== '/platform/marketplace-connection' && path !== '/platform/product-intelligence' && path !== '/platform/content-studio' && path !== '/platform/creative-studio' && path !== '/platform/catalog-operations' && path !== '/platform/revenue-intelligence' && path !== '/platform/supply-chain' && path !== '/platform/customer-operations' && path !== '/platform/financial-intelligence' && path !== '/platform/platform-administration' && path !== '/platform/executive-intelligence' && path !== '/platform/automation-studio' && path !== '/platform/knowledge-center' && path !== '/platform/mobile-companion' && path !== '/platform/unified-commerce' && path !== '/platform/customer-success' && path !== '/platform/enterprise-integration' && path !== '/platform/enterprise-quality' && path !== '/platform/enterprise-devops' && path !== '/platform/enterprise-acceptance' && path !== '/platform/enterprise-beta' && path !== '/platform/enterprise-ga' && path !== '/platform/master-constitution' && path !== '/platform/architecture-governance' && path !== '/platform/engineering-governance' && path !== '/platform/ai-governance' && path !== '/platform/security-governance' && path !== '/platform/product-governance' && path !== '/platform/data-governance' && path !== '/platform/release-governance' && path !== '/platform/master-development-plan' && path !== '/platform/master-dependency-matrix' && path !== '/platform/coding-constitution' && path !== '/platform/repository-blueprint' && path !== '/platform/dev-environment-bootstrap' && path !== '/amazon/inventory' && path !== '/amazon/orders' && path !== '/amazon/order-items' && path !== '/amazon/spapi-v2/orders' && path !== '/amazon/spapi-v2/order-items' && path !== '/amazon/finance' && path !== '/amazon/pricing' && path !== '/amazon/growth' && path !== '/amazon/advertising' && path !== '/amazon/inventory-intel' && path !== '/amazon/pricing-intel' && path !== '/amazon/market-intel' && path !== '/amazon/voc-intel' && path !== '/amazon/content-intel' && path !== '/amazon/search-intel' && path !== '/amazon/experiment-intel' && path !== '/executive/daily-ops' && path !== '/operations/hub' && path !== '/finance/intelligence' && path !== '/strategy/growth-planner' && path !== '/strategy/brand-portfolio' && path !== '/ai/listing' && path !== '/ai/creative' && path !== '/publishing' && path !== '/analytics' && path !== '/workflows' && path !== '/copilot' && path !== '/integrations' && path !== '/billing' && path !== '/developer' && path !== '/knowledge' && path !== '/observability' && path !== '/saas' && path !== '/governance' && path !== '/sre' && path !== '/command').map(([path, meta]) => (
              <Route
                key={path}
                path={path}
                element={<ModulePlaceholderPage {...meta} />}
              />
            ))}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
    </AppShell>
  );
}
